/**
 * Inventaire Tournant Avancé — composant principal connecté au backend.
 *
 * Onglets : Tableau de bord · Planning · Saisie · Actions correctives · Paramètres
 *
 * Utilise les endpoints /api/inventory/* (cf. backend/routers/inventory.py).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, AlertCircle, CalendarDays, CheckCircle2, ClipboardList,
  Loader2, MessageCircle, Plus, RefreshCw, Search, Settings as SettingsIcon,
  Trash2, TrendingUp, Users, X, HelpCircle, BookOpen, ScanLine, Camera, Mic, Lock, ShieldCheck, Layers, Gauge, Scale, Pencil,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import BarcodeScannerDialog from './BarcodeScannerDialog';
import ExpirationOcrDialog from './ExpirationOcrDialog';
import EvidencePhotoUploader from './EvidencePhotoUploader';
import PlanningWhatsAppDialog from './PlanningWhatsAppDialog';
import VoiceSaisieDialog from './VoiceSaisieDialog';
import LotsDialog from './LotsDialog';
import InventoryPilotageReport from './InventoryPilotageReport';
import InventoryStockAuditDialog from './InventoryStockAuditDialog';
import InventoryReferentValidationCard from './InventoryReferentValidationCard';
import { useAuth } from '../../../contexts/AuthContext';
import { playCelebration, playScanBeep } from '../../../utils/soundFeedback';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fcfa = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(Math.abs(n || 0))) + ' FCFA';

const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUT_BADGE = {
  a_faire: { label: 'À faire', cls: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  en_cours: { label: 'En cours', cls: 'bg-orange-100 text-[#C45C26] border-orange-200' },
  termine: { label: 'Terminé', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  en_retard: { label: 'En retard', cls: 'bg-red-100 text-red-700 border-red-200' },
  corrige: { label: 'Corrigé', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  non_corrige: { label: 'Non corrigé', cls: 'bg-red-100 text-red-700 border-red-200' },
};

// ─────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────
const Dashboard = ({ kpi, unassigned, onReload, onGoSettings }) => {
  if (!kpi) return null;
  return (
    <div className="space-y-5" data-testid="inventaire-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-serif font-semibold text-zinc-900">Tableau de bord</h2>
          <p className="text-sm text-zinc-500">Vue synthétique — lecture en moins de 30 secondes</p>
        </div>
        <Button variant="outline" size="sm" onClick={onReload}>
          <RefreshCw className="w-4 h-4 mr-2" /> Recharger
        </Button>
      </div>

      {unassigned && unassigned.count > 0 && (
        <div
          className="rounded-lg bg-red-50 border border-red-200 p-4 flex items-start gap-3"
          data-testid="kpi-unassigned-banner"
        >
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wide text-red-700 font-semibold">
              {unassigned.count} rayon(s) sans responsable permanent
            </p>
            <p className="text-sm text-zinc-700 mt-1 break-words">
              {unassigned.zones.slice(0, 6).map(z => z.name).join(' · ')}
              {unassigned.zones.length > 6 ? ` … +${unassigned.zones.length - 6}` : ''}
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              La génération de cycle est bloquée tant qu'un responsable n'est pas assigné à chaque rayon.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onGoSettings}
            className="border-red-300 text-red-700 hover:bg-red-100"
            data-testid="kpi-unassigned-cta"
          >
            Assigner
          </Button>
        </div>
      )}

      <div className="rounded-lg bg-orange-50 border border-orange-200 p-4 flex items-start gap-3" data-testid="kpi-non-retrouves">
        <AlertTriangle className="w-5 h-5 text-[#C45C26] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wide text-[#C45C26] font-semibold">Écarts non retrouvés</p>
          <p className="text-2xl font-mono font-bold text-zinc-900 mt-0.5 tabular-nums">{fcfa(kpi.ecarts_non_retrouves_fcfa)}</p>
          <p className="text-xs text-zinc-500 mt-0.5">{kpi.ecarts_non_retrouves_nb} produits jamais retrouvés</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiBox label="Écarts ce mois" value={fcfa(kpi.ecart_total_mois_fcfa)} />
        <KpiBox label="Actions à faire" value={kpi.actions_a_faire} />
        <KpiBox label="Actions en retard" value={kpi.actions_overdue} tone={kpi.actions_overdue > 0 ? 'warning' : 'default'} />
        <KpiBox label="Délai moyen" value={kpi.avg_correction_hours ? `${kpi.avg_correction_hours} h` : '—'} />
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-zinc-900 mb-3">Top zones avec écarts</h3>
        {(!kpi.top_zones || !kpi.top_zones.length) ? (
          <p className="text-sm text-zinc-500">Aucun écart enregistré.</p>
        ) : (
          <div className="space-y-2">
            {kpi.top_zones.map((z, i) => (
              <div key={z.name} className="flex items-center gap-3 py-1.5 border-b border-zinc-100 last:border-0">
                <span className="w-5 text-xs text-zinc-400 font-mono">{i + 1}</span>
                <span className="flex-1 text-sm text-zinc-900">{z.name}</span>
                <span className="text-xs text-zinc-500">{z.ecarts} écarts</span>
                <span className="font-mono text-sm tabular-nums text-zinc-700 w-32 text-right">{fcfa(z.valeur)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const KpiBox = ({ label, value, tone = 'default' }) => {
  const tones = {
    default: 'bg-white border-zinc-200',
    warning: 'bg-orange-50 border-orange-200',
    success: 'bg-emerald-50 border-emerald-200',
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">{label}</p>
      <p className="text-2xl font-mono font-bold text-zinc-900 mt-1 tabular-nums">{value}</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Planning
// ─────────────────────────────────────────────────────────
const PlanningView = ({ plannings, agents, zones, onCreate, onDelete, onStart, onReset, onGenerateCycle, reloading, currentUser, isPrivileged, onRefreshStock, onReload }) => {
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refreshingStock, setRefreshingStock] = useState(false);
  const [showAll, setShowAll] = useState(false);  // false = aujourd'hui uniquement
  // Pour les agents (non admin/manager) : filtre auto sur leurs propres affectations
  // Toggle disponible pour qu'ils puissent voir tout le planning de l'équipe s'ils le souhaitent.
  const [onlyMine, setOnlyMine] = useState(!isPrivileged);
  const [waPlanning, setWaPlanning] = useState(null);  // planning sélectionné pour rappel WhatsApp
  const [editPlanning, setEditPlanning] = useState(null);  // planning ouvert dans la modale d'édition
  const [sortOrder, setSortOrder] = useState('desc');  // 'desc' = récent→lointain (défaut), 'asc' = chronologique ancien→futur
  const today = new Date().toISOString().slice(0, 10);
  const myId = currentUser?.id;
  const isMine = (p) => myId && (p.responsable_id === myId || p.op1_id === myId || p.op2_id === myId);

  const scopedPlannings = (plannings || []).filter(p => !onlyMine || isMine(p));
  // Tri configurable : ASC (chronologique) ou DESC (récent en haut).
  scopedPlannings.sort((a, b) => {
    const cmp = (a.date || '').localeCompare(b.date || '');
    return sortOrder === 'asc' ? cmp : -cmp;
  });
  const visiblePlannings = showAll
    ? scopedPlannings
    : scopedPlannings.filter(p => p.date === today || (p.statut === 'en_retard') || (p.statut === 'en_cours'));
  return (
    <div className="space-y-4" data-testid="inventaire-planning">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-serif font-semibold text-zinc-900">
            Planning inventaires
            {onlyMine && <span className="ml-2 text-xs font-sans font-medium px-2 py-0.5 rounded-full bg-purple-100 text-[#5B3B8C] align-middle">👤 Mes inventaires</span>}
          </h2>
          <p className="text-sm text-zinc-500">
            {showAll
              ? `${scopedPlannings.length} planning${scopedPlannings.length > 1 ? 's' : ''} — vue complète${onlyMine ? ' (mes affectations)' : ''}`
              : `Aujourd'hui · ${visiblePlannings.length} inventaire(s) à faire (+ retards & en cours)`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Toggle "Mes inventaires" — disponible pour tous, par défaut activé pour les agents */}
          <button
            onClick={() => setOnlyMine(s => !s)}
            className={`text-xs px-3 py-1.5 rounded-md border transition ${
              onlyMine
                ? 'bg-purple-50 border-purple-200 text-[#5B3B8C]'
                : 'bg-zinc-100 border-zinc-300 text-zinc-700'
            }`}
            data-testid="planning-toggle-mine"
            title={onlyMine ? 'Afficher tout le planning' : 'Afficher uniquement mes affectations'}
          >
            {onlyMine ? '👤 Mes inventaires' : '👥 Toute l\'équipe'}
          </button>
          <button
            onClick={() => setSortOrder(s => s === 'desc' ? 'asc' : 'desc')}
            className="text-xs px-3 py-1.5 rounded-md border bg-zinc-100 border-zinc-300 text-zinc-700 hover:bg-zinc-200 transition"
            data-testid="planning-toggle-sort"
            title={sortOrder === 'desc' ? 'Cliquer pour trier par date croissante (chronologique)' : 'Cliquer pour trier par date décroissante (récent en haut)'}
          >
            {sortOrder === 'desc' ? '🔽 Récent → Lointain' : '🔼 Ancien → Futur'}
          </button>
          <button
            onClick={async () => {
              setRefreshingStock(true);
              try { await onRefreshStock(); }
              finally { setRefreshingStock(false); }
            }}
            disabled={refreshingStock}
            className="text-xs px-3 py-1.5 rounded-md border bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition disabled:opacity-60 disabled:cursor-wait inline-flex items-center gap-1.5"
            data-testid="planning-refresh-stock"
            title="Synchroniser le stock théorique avec Mediciel temps réel"
          >
            {refreshingStock
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sync en cours…</>
              : <><RefreshCw className="w-3.5 h-3.5" /> Actualiser stock</>}
          </button>
          <button
            onClick={() => setShowAll(s => !s)}
            className={`text-xs px-3 py-1.5 rounded-md border transition ${
              showAll
                ? 'bg-zinc-100 border-zinc-300 text-zinc-700'
                : 'bg-emerald-50 border-emerald-200 text-emerald-700'
            }`}
            data-testid="planning-toggle-scope"
          >
            {showAll ? '📅 Vue complète' : `📍 Aujourd'hui uniquement`}
          </button>
          {isPrivileged && (
            <>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const dry = await axios.post(
                      `${API}/inventory/plannings/reassign-excluded-ops?dry_run=true`,
                      {},
                      { headers: auth() },
                    );
                    const n = dry.data?.reassigned || 0;
                    if (n === 0) {
                      toast.info('Aucun planning à réassigner (les managers exclus ne sont pas dans les plannings actifs).');
                      return;
                    }
                    const sample = (dry.data?.items || []).slice(0, 5).map((it) => {
                      const op = it.op1_name_to ? `OP1 ${it.op1_name_from}→${it.op1_name_to}` : `OP2 ${it.op2_name_from}→${it.op2_name_to}`;
                      return `• ${it.date} ${it.zone_name} (${op})`;
                    }).join('\n');
                    if (!window.confirm(
                      `Réassigner ${n} planning(s) où Cyrille (ou un manager exclu) est OP1/OP2 ?\n\n${sample}${n > 5 ? `\n…et ${n - 5} de plus` : ''}\n\nSeuls les plannings non démarrés sont touchés.`,
                    )) return;
                    const res = await axios.post(
                      `${API}/inventory/plannings/reassign-excluded-ops?dry_run=false`,
                      {},
                      { headers: auth() },
                    );
                    toast.success(`${res.data?.reassigned || 0} planning(s) réassigné(s).`, { duration: 6000 });
                    // reload planning list
                    if (typeof onRefreshStock === 'function') {
                      // pas exactement, on force un reload via parent — au pire l'utilisateur rafraîchit la page
                    }
                    window.location.reload();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Erreur réassignation');
                  }
                }}
                data-testid="planning-reassign-excluded-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                title="Retire Cyrille (et autres managers exclus) des plannings non démarrés et le remplace par un agent éligible"
              >
                <Users className="w-4 h-4 mr-2" /> Réassigner Cyrille
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  const monday = window.prompt('Date du lundi de la semaine 1 (YYYY-MM-DD) :', new Date().toISOString().slice(0, 10));
                  if (!monday) return;
                  const cyclesStr = window.prompt('Nombre de cycles à générer (1 cycle = 6 semaines) :', '2');
                  const cycles = parseInt(cyclesStr, 10);
                  if (!cycles || cycles < 1) return;
                  setGenerating(true);
                  try {
                    await onGenerateCycle(monday, cycles);
                  } finally { setGenerating(false); }
                }}
                disabled={generating}
                data-testid="planning-generate-cycle-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarDays className="w-4 h-4 mr-2" />}
                Générer cycle 6 semaines
              </Button>
              <Button onClick={() => setCreating(true)} className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white" data-testid="planning-create-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouveau planning
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Zone</th>
                <th className="px-3 py-2 font-medium">Responsable</th>
                <th className="px-3 py-2 font-medium">Op 1</th>
                <th className="px-3 py-2 font-medium">Op 2</th>
                <th className="px-3 py-2 font-medium">Statut</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visiblePlannings.map((p) => {
                const s = STATUT_BADGE[p.statut] || STATUT_BADGE.a_faire;
                return (
                  <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-2 font-mono text-zinc-700">{p.date}</td>
                    <td className="px-3 py-2 font-medium text-zinc-900">{p.zone_name}</td>
                    <td className="px-3 py-2 text-zinc-700">
                      {p.responsable_name}
                      {p.responsable_absent_planned && (
                        <span
                          className="ml-1.5 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded"
                          title="Responsable absent ce jour — validation différée"
                          data-testid={`planning-resp-absent-${p.id}`}
                        >
                          différé
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-700">{p.op1_name}</td>
                    <td className="px-3 py-2 text-zinc-700">{p.op2_name}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setWaPlanning(p)}
                        className="group inline-flex flex-col items-start gap-0.5 cursor-pointer focus:outline-none"
                        title="Cliquer pour envoyer un rappel WhatsApp"
                        data-testid={`planning-status-btn-${p.id}`}
                      >
                        <div className="inline-flex items-center gap-1.5">
                          <Badge className={`${s.cls} border group-hover:ring-2 group-hover:ring-emerald-300 transition`}>{s.label}</Badge>
                          <MessageCircle className="w-3.5 h-3.5 text-zinc-400 group-hover:text-emerald-600 transition" />
                        </div>
                        {p.step_label && p.step !== 'closed' && (
                          <span
                            className="text-[11px] text-zinc-600 leading-tight text-left"
                            data-testid={`planning-step-label-${p.id}`}
                          >
                            {p.step_label}
                            {p.expected_person_name ? ` : ${p.expected_person_name}` : ''}
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => onStart(p)} className="mr-1" data-testid={`planning-start-${p.id}`}>
                        {p.statut === 'termine' ? 'Voir' : 'Saisir'}
                      </Button>
                      {isPrivileged && p.statut !== 'termine' && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setEditPlanning(p)}
                          className="text-indigo-600 hover:bg-indigo-50 h-8 w-8 mr-1"
                          data-testid={`planning-edit-${p.id}`}
                          title="Modifier l'équipe (responsable / OP1 / OP2). Les rôles déjà en cours sont verrouillés."
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {p.statut === 'en_cours' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onReset(p)}
                          className="mr-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`planning-reset-${p.id}`}
                          title="Annuler l'inventaire en cours et restaurer le planning"
                        >
                          Réinitialiser
                        </Button>
                      )}
                      {p.statut !== 'termine' && (
                        <Button size="icon" variant="ghost" onClick={() => onDelete(p.id)} className="text-red-600 h-8 w-8">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!visiblePlannings.length && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500">
                  {reloading ? 'Chargement…' : (
                    showAll
                      ? 'Aucun planning'
                      : `Aucun inventaire à faire aujourd'hui (${today}). Cliquez « Vue complète » pour voir tous les cycles.`
                  )}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <PlanningCreateDialog
          zones={zones}
          agents={agents}
          onClose={() => setCreating(false)}
          onSubmit={async (data) => { await onCreate(data); setCreating(false); }}
        />
      )}

      {waPlanning && (
        <PlanningWhatsAppDialog
          planning={waPlanning}
          agents={agents}
          onClose={() => setWaPlanning(null)}
        />
      )}

      {editPlanning && (
        <PlanningEditDialog
          planning={editPlanning}
          agents={agents}
          onClose={() => setEditPlanning(null)}
          onSaved={() => { setEditPlanning(null); onReload && onReload(); }}
        />
      )}
    </div>
  );
};

const PlanningEditDialog = ({ planning, agents, onClose, onSaved }) => {
  // Filtre les agents disponibles à la date du planning (pas en congé, pas jour repos, pas manager)
  const planningDate = planning.date ? new Date(planning.date + 'T00:00:00') : null;
  const dowJs = planningDate ? planningDate.getDay() : null; // 0=Dim, 1=Lun, ..., 6=Sam
  const dowPy = dowJs === null ? null : (dowJs === 0 ? 6 : dowJs - 1); // Conv en 0=Lun, ..., 6=Dim
  const monthIdx = planningDate ? (planningDate.getMonth() + 1) : null;

  // Récupère le statut de l'inventaire (si démarré) pour savoir quels rôles verrouiller
  const [invStatus, setInvStatus] = React.useState(null);
  React.useEffect(() => {
    if (!planning.inventory_id) { setInvStatus('a_faire'); return; }
    axios.get(`${API}/inventory/inventories/${planning.inventory_id}`, { headers: auth() })
      .then(r => setInvStatus(r.data?.status || null))
      .catch(() => setInvStatus(null));
  }, [planning.inventory_id]);

  const op1Locked = invStatus && ['en_cours_op1', 'cloture_op1', 'en_cours_op2', 'cloture_op2', 'commentaire_referent', 'validation_manager_requise'].includes(invStatus);
  // OP2 reste swappable tant que la phase OP2 n'a pas été clôturée (cas typique : OP2 prévu en congé, phase ouverte sans saisie).
  const op2Locked = invStatus && ['cloture_op2', 'commentaire_referent', 'validation_manager_requise'].includes(invStatus);
  const agentEligibleOp = (a) => {
    if (!a) return false;
    if (a.role === 'manager') return false;
    if (a.inventory_exclude_op) return false;
    if (a.inventory_rest_day_of_week !== null && a.inventory_rest_day_of_week !== undefined && a.inventory_rest_day_of_week === dowPy) return false;
    if ((a.inventory_vacation_months || []).includes(monthIdx)) return false;
    return true;
  };
  const agentEligibleResp = (a) => {
    if (!a) return false;
    if (a.inventory_rest_day_of_week !== null && a.inventory_rest_day_of_week !== undefined && a.inventory_rest_day_of_week === dowPy) return false;
    if ((a.inventory_vacation_months || []).includes(monthIdx)) return false;
    return true;
  };
  const eligibleOp = (agents || []).filter(agentEligibleOp);
  const eligibleResp = (agents || []).filter(agentEligibleResp);

  const [respId, setRespId] = React.useState(planning.responsable_id || '');
  const [op1Id, setOp1Id] = React.useState(planning.op1_id || '');
  const [op2Id, setOp2Id] = React.useState(planning.op2_id || '');
  const [planDate, setPlanDate] = React.useState(planning.date || '');
  const [saving, setSaving] = React.useState(false);

  // Date modifiable UNIQUEMENT si l'inventaire n'est pas démarré
  const dateLocked = invStatus && invStatus !== 'a_faire';

  const save = async () => {
    setSaving(true);
    try {
      const body = { responsable_id: respId || null };
      if (!op1Locked) body.op1_id = op1Id || null;
      if (!op2Locked) body.op2_id = op2Id || null;
      if (!dateLocked && planDate && planDate !== planning.date) body.date = planDate;
      const res = await axios.put(
        `${API}/inventory/plannings/${planning.id}`, body,
        { headers: auth() },
      );
      if (res.data?.changed) {
        toast.success(`Planning modifié : ${(res.data.changes || []).join(', ') || 'OK'}`, { duration: 6000 });
      } else {
        toast.info('Aucun changement.');
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" data-testid="planning-edit-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-zinc-900">Modifier l&apos;équipe</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-700"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-zinc-600 mb-1">
          {planning.zone_name} · {planning.date}
        </p>
        {invStatus && invStatus !== 'a_faire' && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
            Inventaire en cours (<strong>{invStatus}</strong>). Les rôles déjà engagés sont verrouillés.
          </p>
        )}
        <div className="space-y-3">
          <label className="block text-sm">
            <span className={`font-medium ${dateLocked ? 'text-zinc-400' : 'text-zinc-700'}`}>
              Date {dateLocked && '🔒'}
            </span>
            <input
              type="date"
              value={planDate}
              onChange={(e) => setPlanDate(e.target.value)}
              disabled={dateLocked}
              className="mt-1 w-full rounded border border-zinc-300 px-2 py-1.5 text-sm disabled:bg-zinc-100 disabled:text-zinc-500"
              data-testid="planning-edit-date"
            />
            {dateLocked
              ? <span className="text-[11px] text-zinc-500">Inventaire démarré : date verrouillée.</span>
              : <span className="text-[11px] text-zinc-500">Une seule date libre — pas d&apos;autre inventaire ce jour-là.</span>}
          </label>
          <label className="block text-sm">
            <span className="text-zinc-700 font-medium">Responsable</span>
            <select
              value={respId}
              onChange={(e) => setRespId(e.target.value)}
              className="mt-1 w-full rounded border border-zinc-300 px-2 py-1.5 text-sm"
              data-testid="planning-edit-responsable"
            >
              <option value="">— Aucun —</option>
              {eligibleResp.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className={`font-medium ${op1Locked ? 'text-zinc-400' : 'text-zinc-700'}`}>
              OP1 (comptage) {op1Locked && '🔒'}
            </span>
            <select
              value={op1Id}
              onChange={(e) => setOp1Id(e.target.value)}
              disabled={op1Locked}
              className="mt-1 w-full rounded border border-zinc-300 px-2 py-1.5 text-sm disabled:bg-zinc-100 disabled:text-zinc-500"
              data-testid="planning-edit-op1"
            >
              <option value="">— Aucun —</option>
              {eligibleOp.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            {op1Locked && <span className="text-[11px] text-zinc-500">OP1 a démarré son comptage : verrouillé.</span>}
          </label>
          <label className="block text-sm">
            <span className={`font-medium ${op2Locked ? 'text-zinc-400' : 'text-zinc-700'}`}>
              OP2 (recomptage / écarts) {op2Locked && '🔒'}
            </span>
            <select
              value={op2Id}
              onChange={(e) => setOp2Id(e.target.value)}
              disabled={op2Locked}
              className="mt-1 w-full rounded border border-zinc-300 px-2 py-1.5 text-sm disabled:bg-zinc-100 disabled:text-zinc-500"
              data-testid="planning-edit-op2"
            >
              <option value="">— Aucun —</option>
              {eligibleOp.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            {op2Locked && <span className="text-[11px] text-zinc-500">OP2 a démarré : verrouillé.</span>}
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white"
            data-testid="planning-edit-save"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

const PlanningCreateDialog = ({ zones, agents, onClose, onSubmit }) => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [zoneId, setZoneId] = useState('');
  const [resp, setResp] = useState('');
  const [op1, setOp1] = useState('');
  const [op2, setOp2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const valid = date && zoneId && resp && op1 && op2 && new Set([resp, op1, op2]).size === 3;
  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit({ date, zone_id: zoneId, responsable_id: resp, op1_id: op1, op2_id: op2 });
    } finally { setSubmitting(false); }
  };
  const Selector = ({ value, onChange, label, exclude = [], testId }) => (
    <div>
      <label className="text-xs text-zinc-500 mb-1 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-300 rounded-md p-2 text-sm" data-testid={testId}>
        <option value="">— Choisir —</option>
        {agents.filter(a => !exclude.includes(a.id)).map(a => (
          <option key={a.id} value={a.id}>{a.name} {a.role ? `· ${a.role}` : ''}</option>
        ))}
      </select>
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="planning-create-dialog">
        <DialogHeader><DialogTitle className="font-serif">Nouveau planning</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Zone</label>
            <select value={zoneId} onChange={(e) => setZoneId(e.target.value)} className="w-full border border-zinc-300 rounded-md p-2 text-sm" data-testid="planning-zone-select">
              <option value="">— Choisir —</option>
              {zones.map(z => <option key={z.id} value={z.id}>{z.name}{z.is_critical ? ' · critique' : ''}</option>)}
            </select>
          </div>
          <Selector value={resp} onChange={setResp} label="Responsable" exclude={[op1, op2].filter(Boolean)} testId="planning-resp-select" />
          <Selector value={op1} onChange={setOp1} label="Opérateur 1 (compteur)" exclude={[resp, op2].filter(Boolean)} testId="planning-op1-select" />
          <Selector value={op2} onChange={setOp2} label="Opérateur 2 (recompteur)" exclude={[resp, op1].filter(Boolean)} testId="planning-op2-select" />
          {!valid && (date || zoneId || resp || op1 || op2) && (
            <p className="text-xs text-[#C45C26]">Tous les champs sont requis et les 3 personnes doivent être différentes.</p>
          )}
          <DialogFooter className="flex-row justify-between">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={!valid || submitting} className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white" data-testid="planning-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────
// Saisie inventaire
// ─────────────────────────────────────────────────────────
// ── Step indicator dans le bandeau de phase ──────────────────────────────
const PhaseStep = ({ num, label, who, state, meta }) => {
  // state : 'todo' (gris) · 'next' (bleu pâle, à venir) · 'active' (highlight) · 'done' (vert)
  const styles = {
    todo:   { bar: 'bg-zinc-200',   num: 'bg-zinc-200 text-zinc-500', text: 'text-zinc-500' },
    next:   { bar: 'bg-blue-200',   num: 'bg-blue-200 text-blue-800', text: 'text-blue-800' },
    active: { bar: 'bg-amber-400',  num: 'bg-amber-500 text-white',   text: 'text-amber-900' },
    done:   { bar: 'bg-emerald-400', num: 'bg-emerald-500 text-white', text: 'text-emerald-800' },
  }[state] || { bar: 'bg-zinc-200', num: 'bg-zinc-200 text-zinc-500', text: 'text-zinc-500' };
  return (
    <div className="flex flex-col gap-1.5" data-testid={`phase-step-${num}`}>
      <div className={`h-1 rounded ${styles.bar}`} />
      <div className="flex items-center gap-1.5">
        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${styles.num}`}>{num}</span>
        <span className={`font-semibold ${styles.text}`}>{label}</span>
      </div>
      <p className="text-zinc-600 leading-tight truncate">{who || '—'}</p>
      {meta && <p className="text-[10px] text-zinc-400 leading-tight">{meta}</p>}
    </div>
  );
};

const SaisieView = ({ inventoryId, agents, enums, onClose, onCloseInventory, currentUser, canManagePhases }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ecartLine, setEcartLine] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [ocrLineId, setOcrLineId] = useState(null);
  const [voiceLineId, setVoiceLineId] = useState(null);
  const [lotsLineId, setLotsLineId] = useState(null);
  const [highlightLineId, setHighlightLineId] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/inventories/${inventoryId}`, { headers: auth() });
      setData(res.data);
    } catch (e) { toast.error('Erreur chargement inventaire'); }
    finally { setLoading(false); }
  }, [inventoryId]);
  useEffect(() => { load(); }, [load]);

  const submitPhysique = async (lineId, value) => {
    try {
      const res = await axios.put(
        `${API}/inventory/inventories/${inventoryId}/lines/${lineId}`,
        { stock_physique: parseInt(value, 10) },
        { headers: auth() }
      );
      setData(d => ({ ...d, lines: d.lines.map(l => l.id === lineId ? res.data : l) }));
    } catch (e) { toast.error("Erreur d'enregistrement"); }
  };

  const submitExpirationDate = async (lineId, isoDate) => {
    try {
      const res = await axios.put(
        `${API}/inventory/inventories/${inventoryId}/lines/${lineId}`,
        { physical_expiration_date: isoDate || '' },
        { headers: auth() }
      );
      setData(d => ({ ...d, lines: d.lines.map(l => l.id === lineId ? res.data : l) }));
      if (isoDate) toast.success(`Péremption enregistrée : ${isoDate}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur péremption"); }
  };

  // Saisie vocale combinée : applique en parallèle quantité + péremption
  // (chaque champ ne déclenche un appel API que s'il a été reconnu)
  const applyVoiceCommand = async (lineId, { quantity, expiration }) => {
    setVoiceLineId(null);
    const ops = [];
    if (quantity !== null && quantity !== undefined) ops.push(submitPhysique(lineId, quantity));
    if (expiration) ops.push(submitExpirationDate(lineId, expiration));
    if (!ops.length) {
      toast.error('Aucune valeur reconnue. Réessayez.');
      return;
    }
    await Promise.all(ops);
    playScanBeep();
    const parts = [];
    if (quantity !== null && quantity !== undefined) parts.push(`Qté ${quantity}`);
    if (expiration) parts.push(`Pér. ${expiration}`);
    toast.success(`Vocal · ${parts.join(' · ')}`);
  };

  // Saisie multi-lots : remplace tous les lots de la ligne
  const submitLots = async (lineId, lotsPayload) => {
    try {
      const res = await axios.put(
        `${API}/inventory/inventories/${inventoryId}/lines/${lineId}`,
        { lots: lotsPayload },
        { headers: auth() }
      );
      setData(d => ({ ...d, lines: d.lines.map(l => l.id === lineId ? res.data : l) }));
      const total = res.data?.stock_physique ?? 0;
      toast.success(`${lotsPayload.length} lot(s) enregistré(s) · Total ${total}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement lots");
      throw e; // pour que le dialog garde l'état d'erreur
    }
  };

  const onBarcodeDetected = (code) => {
    setScanOpen(false);
    if (!data?.lines) return;
    const norm = (s) => (s || '').replace(/\D/g, '');
    const target = norm(code);
    const line = data.lines.find(l => norm(l.product_code_ean) === target);
    if (!line) {
      toast.error(`Aucun produit avec le code-barres ${code}`);
      return;
    }
    setSearch(line.product_name); // filtre la table sur ce produit
    setHighlightLineId(line.id);
    playScanBeep();  // 🔔 feedback sonore + vibration : scan EAN réussi
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="saisie-input-${line.id}"]`);
      if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); el.focus(); }
    }, 200);
    setTimeout(() => setHighlightLineId(null), 2500);
    toast.success(`Trouvé : ${line.product_name}`);
  };

  const closeOp1 = async () => {
    try {
      await axios.post(`${API}/inventory/inventories/${inventoryId}/close-op1`, {}, { headers: auth() });
      toast.success('✅ Comptage OP1 fermé. OP2 peut maintenant ouvrir son job.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec fermeture OP1');
    }
  };

  const openOp2 = async () => {
    try {
      await axios.post(`${API}/inventory/inventories/${inventoryId}/open-op2`, {}, { headers: auth() });
      toast.success('🔄 Phase OP2 ouverte — traitement des écarts');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec ouverture OP2');
    }
  };

  const closeOp2 = async () => {
    try {
      await axios.post(`${API}/inventory/inventories/${inventoryId}/close-op2`, {}, { headers: auth() });
      toast.success('✅ Traitement OP2 fermé. Le responsable peut clôturer l\'inventaire.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec fermeture OP2');
    }
  };

  const closeInventory = async () => {
    try {
      const res = await axios.post(`${API}/inventory/inventories/${inventoryId}/close`, {}, { headers: auth() });
      const { total_ecart_count, total_ecart_value_fcfa, encouragements = [] } = res.data;
      // 🎉 Son de célébration + vibration (motivation agent)
      playCelebration();
      toast.success(`🎉 Inventaire clôturé · ${total_ecart_count} écart(s) · ${fcfa(total_ecart_value_fcfa)}`);
      // Auto-open WhatsApp encouragement messages for op1 + op2 (free, manual click-to-chat)
      if (encouragements.length) {
        if (window.confirm(
          `Envoyer un message d'encouragement WhatsApp à ${encouragements.map(e => e.agent).join(' et ')} ? `
          + `${encouragements.length} fenêtre(s) WhatsApp s'ouvriront — vous cliquez sur Envoyer dans chacune.`
        )) {
          encouragements.forEach((e, i) => {
            setTimeout(() => window.open(e.url, '_blank', 'noopener,noreferrer'), i * 500);
          });
        }
      }
      onCloseInventory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la clôture');
    }
  };

  // Met toutes les lignes non saisies à 0 d'un coup (pour les rayons partiellement vides).
  // Utile pour clôturer rapidement quand seuls quelques produits sont en stock.
  const fillAllEmptyToZero = async () => {
    const empties = (data.lines || []).filter(l => l.stock_physique === null || l.stock_physique === undefined);
    if (!empties.length) {
      toast.info('Toutes les lignes sont déjà saisies');
      return;
    }
    if (!window.confirm(
      `Mettre les ${empties.length} ligne(s) non saisie(s) à 0 ? `
      + `(Cela signifie que ces produits sont absents du rayon.)`
    )) return;
    let ok = 0; let fail = 0;
    for (const l of empties) {
      try {
        await axios.put(
          `${API}/inventory/inventories/${inventoryId}/lines/${l.id}`,
          { stock_physique: 0 },
          { headers: auth() }
        );
        ok++;
      } catch (_) { fail++; }
    }
    if (ok) toast.success(`${ok} ligne(s) mises à 0`);
    if (fail) toast.error(`${fail} échec(s)`);
    await load();
  };

  const lines = useMemo(() => {
    if (!data?.lines) return [];
    if (!search.trim()) return data.lines;
    const q = search.toLowerCase();
    return data.lines.filter(l =>
      (l.product_name || '').toLowerCase().includes(q) ||
      (l.product_code_ean || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1B4D3E]" /></div>;
  if (!data) return null;

  const inv = data.inventory;
  const saisis = (data.lines || []).filter(l => l.stock_physique !== null && l.stock_physique !== undefined).length;
  const totalLines = (data.lines || []).length;
  const nonSaisis = totalLines - saisis;
  const ecartTotal = (data.lines || []).reduce((s, l) => s + (l.valeur_ecart_fcfa || 0), 0);
  const ecartLines = (data.lines || []).filter(l => l.ecart !== null && l.ecart !== 0);
  const pendingEcarts = ecartLines.filter(l => !l.discrepancy_id).length;

  // ── Workflow en chaîne : OP1 → OP2 → Responsable ─────────────────────────
  // Statuts : en_cours_op1 → cloture_op1 → en_cours_op2 → cloture_op2 →
  //           [commentaire_referent → validation_manager_requise] →
  //           termine_ok / termine_avec_ecarts
  const status = inv.status;
  const isFinalDone = status === 'termine_ok' || status === 'termine_avec_ecarts';
  const isClosed = isFinalDone;
  const phaseOp1Active = status === 'en_cours_op1' || status === 'en_cours';
  const phaseOp2Active = status === 'en_cours_op2';
  const phaseRespActive = status === 'cloture_op2';
  const phaseReferentActive = status === 'commentaire_referent';
  const phaseManagerValidation = status === 'validation_manager_requise';
  const phaseReviewActive = phaseReferentActive || phaseManagerValidation;
  const waitingOp2 = status === 'cloture_op1';

  const myId = currentUser?.id;
  const isOp1 = myId && myId === inv.op1_id;
  const isOp2 = myId && myId === inv.op2_id;
  const isResp = myId && myId === inv.responsable_id;
  // Manager + Admin peuvent intervenir à toutes les étapes
  const canActAsOp1 = isOp1 || canManagePhases;
  const canActAsOp2 = isOp2 || canManagePhases;
  const canActAsResp = isResp || canManagePhases;

  // Édition des lignes : autorisée si l'utilisateur peut agir dans la phase en cours
  const canEditCount = phaseOp1Active && canActAsOp1;
  const canEditEcarts = phaseOp2Active && canActAsOp2;
  const linesEditable = !isClosed && (canEditCount || canEditEcarts || canManagePhases);

  return (
    <div className="space-y-4 pb-44" data-testid="inventaire-saisie">
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Inventaire {isClosed ? '· clôturé' : 'en cours'}</p>
            <h2 className="text-xl font-serif font-semibold text-zinc-900 mt-0.5">{inv.zone_name}</h2>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-zinc-500">
              <span>Op 1 : {inv.op1_name}</span>
              <span>Op 2 : {inv.op2_name}</span>
              <span>Resp : {inv.responsable_name}</span>
              <span>{inv.products_count} produits</span>
              {isClosed && inv.total_ecart_count > 0 && <span className="text-[#C45C26] font-semibold">{inv.total_ecart_count} écart(s) · {fcfa(inv.total_ecart_value_fcfa)}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(status === 'a_faire' || status === 'en_cours' || status === 'en_cours_op1') && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAuditOpen(true)}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="saisie-audit-btn"
                title="Comparer le stock théorique (snapshot) avec le stock Mediciel actuel — disponible tant qu'OP1 n'a pas clôturé"
              >
                <Scale className="w-3.5 h-3.5 mr-1" /> Audit Mediciel
              </Button>
            )}
            {isClosed && inv.total_ecart_count > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const url = `${API}/inventory/inventories/${inventoryId}/export-mediciel?format=xlsx`;
                    const token = localStorage.getItem('dermascan_token');
                    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                      .then(r => r.ok ? r.blob() : Promise.reject(r))
                      .then(blob => {
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = `Synthese_Mediciel_${inv.zone_name}.xlsx`;
                        a.click();
                        URL.revokeObjectURL(a.href);
                        toast.success('Excel téléchargé');
                      })
                      .catch(() => toast.error('Téléchargement Excel échoué'));
                  }}
                  data-testid="saisie-excel-btn"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  title="Synthèse Excel à saisir manuellement dans Mediciel"
                >
                  Excel Mediciel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const url = `${API}/inventory/inventories/${inventoryId}/export-mediciel?format=pdf`;
                    const token = localStorage.getItem('dermascan_token');
                    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                      .then(r => r.ok ? r.blob() : Promise.reject(r))
                      .then(blob => {
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = `Synthese_Mediciel_${inv.zone_name}.pdf`;
                        a.click();
                        URL.revokeObjectURL(a.href);
                        toast.success('PDF téléchargé');
                      })
                      .catch(() => toast.error('Téléchargement PDF échoué'));
                  }}
                  data-testid="saisie-pdf-btn"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  title="Synthèse PDF à saisir manuellement dans Mediciel"
                >
                  PDF Mediciel
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      {/* ───── Indicateur de phase du workflow OP1 → OP2 → Responsable ───── */}
      {!isClosed && (
        <div className="rounded-lg border border-zinc-200 bg-white p-3" data-testid="phase-indicator">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Étape en cours</p>
            <span
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                phaseOp1Active ? 'bg-amber-100 text-amber-900'
                  : waitingOp2 ? 'bg-blue-100 text-blue-900'
                  : phaseOp2Active ? 'bg-orange-100 text-orange-900'
                  : phaseRespActive ? 'bg-emerald-100 text-emerald-900'
                  : 'bg-zinc-100 text-zinc-700'
              }`}
              data-testid="phase-current-badge"
            >
              {phaseOp1Active ? 'OP1 · Comptage' :
               waitingOp2 ? 'En attente OP2' :
               phaseOp2Active ? 'OP2 · Écarts' :
               phaseRespActive ? 'Responsable · Clôture' : status}
            </span>
          </div>
          {/* Steps row */}
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            {/* Step 1 OP1 */}
            <PhaseStep
              num="1"
              label="Comptage OP1"
              who={inv.op1_name}
              state={
                phaseOp1Active ? 'active'
                : (waitingOp2 || phaseOp2Active || phaseRespActive) ? 'done'
                : 'todo'
              }
              meta={inv.op1_closed_at ? `Fermé le ${new Date(inv.op1_closed_at).toLocaleDateString('fr-FR')}` : null}
            />
            {/* Step 2 OP2 */}
            <PhaseStep
              num="2"
              label="Écarts OP2"
              who={inv.op2_name}
              state={
                phaseOp2Active ? 'active'
                : (phaseRespActive) ? 'done'
                : waitingOp2 ? 'next'
                : 'todo'
              }
              meta={inv.op2_closed_at ? `Fermé le ${new Date(inv.op2_closed_at).toLocaleDateString('fr-FR')}` : null}
            />
            {/* Step 3 Resp */}
            <PhaseStep
              num="3"
              label="Clôture Resp."
              who={inv.responsable_name}
              state={phaseRespActive ? 'active' : 'todo'}
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Rechercher un produit…" className="pl-9 h-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="saisie-search-input" />
        </div>
        <Button
          type="button"
          onClick={() => setScanOpen(true)}
          disabled={isClosed}
          className="bg-[#5B3B8C] hover:bg-[#4A2F73] text-white h-9"
          data-testid="saisie-scan-btn"
          title="Scanner code-barres"
        >
          <ScanLine className="w-4 h-4 mr-1.5" />
          <span className="hidden sm:inline">Scanner</span>
        </Button>
      </div>

      {scanOpen && (
        <BarcodeScannerDialog
          onDetect={onBarcodeDetected}
          onClose={() => setScanOpen(false)}
        />
      )}

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-3 py-2 font-medium">Produit</th>
                <th className="px-3 py-2 font-medium">EAN</th>
                <th className="px-3 py-2 font-medium text-right">Théo.</th>
                <th className="px-3 py-2 font-medium w-28">Physique</th>
                <th className="px-3 py-2 font-medium w-44">Péremption</th>
                <th className="px-3 py-2 font-medium text-right">Écart</th>
                <th className="px-3 py-2 font-medium text-right">Valeur</th>
                <th className="px-3 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const e = l.ecart;
                const ecls = e === null ? 'text-zinc-400' : e === 0 ? 'text-emerald-700 font-semibold' : e > 0 ? 'text-[#C45C26] font-semibold' : 'text-red-700 font-semibold';
                // Badge péremption — UNIQUEMENT sur la date saisie physiquement par l'agent
                // (la date Mediciel n'est plus utilisée comme fallback car non confirmée).
                // Si stock_physique = 0, aucun badge (rien à périmer).
                const hasZeroStock = l.stock_physique === 0;
                const expDate = !hasZeroStock ? l.physical_expiration_date : null;
                let expBadge = null;
                if (expDate) {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const exp = new Date(expDate);
                  const diffDays = Math.round((exp - today) / 86400000);
                  // Filet de sécurité Lot B : date passée récente OU < 30 jours = probable confusion FAB↔PER
                  const isSuspectFab = diffDays < 30 && diffDays > -540;
                  if (isSuspectFab) {
                    expBadge = {
                      label: 'Vérifier — fabrication ?',
                      cls: 'bg-purple-50 border-purple-300 text-[#5B3B8C]',
                      hint: 'Cette date est suspecte (passée ou très proche). Vérifiez sur la boîte que vous avez bien lu EXP/PER et non FAB/MFD.',
                    };
                  } else if (diffDays < 0) {
                    expBadge = { label: 'Périmé', cls: 'bg-red-100 border-red-300 text-red-800' };
                  } else if (diffDays < 90) expBadge = { label: '<3 mois', cls: 'bg-orange-100 border-orange-300 text-orange-800' };
                  else if (diffDays < 180) expBadge = { label: '<6 mois', cls: 'bg-amber-50 border-amber-200 text-amber-700' };
                }
                const isNotFilled = l.stock_physique === null || l.stock_physique === undefined;
                const rowCls = highlightLineId === l.id
                  ? 'border-b border-zinc-100 bg-purple-50 ring-2 ring-purple-300'
                  : isNotFilled
                    ? 'border-b border-zinc-100 bg-red-50/40 hover:bg-red-50/60'
                    : 'border-b border-zinc-100 hover:bg-zinc-50';
                return (
                  <tr key={l.id} className={rowCls}>
                    <td className="px-3 py-2 text-zinc-900">{l.product_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-500">{l.product_code_ean}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-700">{l.stock_theorique}</td>
                    <td className="px-3 py-2">
                      <Input
                        type="number" inputMode="numeric"
                        defaultValue={l.stock_physique ?? ''} disabled={isClosed || !canEditCount}
                        onBlur={(ev) => {
                          const v = ev.target.value;
                          if (v !== '' && parseInt(v, 10) !== l.stock_physique) submitPhysique(l.id, v);
                        }}
                        className="font-mono h-8 text-right"
                        data-testid={`saisie-input-${l.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className={`flex items-center gap-1 ${hasZeroStock ? 'opacity-40 pointer-events-none' : ''}`}>
                        <Input
                          type="date"
                          value={hasZeroStock ? '' : (l.physical_expiration_date || '')}
                          disabled={isClosed || hasZeroStock || !canEditCount}
                          onChange={(ev) => submitExpirationDate(l.id, ev.target.value)}
                          className="h-8 text-xs px-1.5 flex-1 min-w-0"
                          data-testid={`saisie-exp-${l.id}`}
                          title={hasZeroStock ? 'Stock à 0 — pas de date de péremption requise' : (l.expiration_date ? `Mediciel : ${l.expiration_date}` : '')}
                        />
                        {!isClosed && (
                          <>
                            <button
                              type="button"
                              onClick={() => setLotsLineId(l.id)}
                              className={`p-1.5 rounded border ${
                                Array.isArray(l.lots) && l.lots.length
                                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                  : 'border-purple-200 text-[#5B3B8C] hover:bg-purple-50'
                              }`}
                              title={
                                Array.isArray(l.lots) && l.lots.length
                                  ? `${l.lots.length} lot(s) saisi(s) · cliquer pour modifier`
                                  : 'Saisir plusieurs lots (dates de péremption différentes)'
                              }
                              data-testid={`saisie-lots-${l.id}`}
                            >
                              <Layers className="w-3.5 h-3.5" />
                              {Array.isArray(l.lots) && l.lots.length > 0 && (
                                <span className="ml-0.5 text-[10px] font-bold">{l.lots.length}</span>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => setVoiceLineId(l.id)}
                              className="p-1.5 rounded border border-purple-200 text-[#5B3B8C] hover:bg-purple-50"
                              title='Saisie vocale (ex: "quantité 2 péremption 04 2030")'
                              data-testid={`saisie-voice-${l.id}`}
                            >
                              <Mic className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setOcrLineId(l.id)}
                              className="p-1.5 rounded border border-purple-200 text-[#5B3B8C] hover:bg-purple-50"
                              title="Scanner la date avec la caméra (OCR)"
                              data-testid={`saisie-exp-ocr-${l.id}`}
                            >
                              <Camera className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => toast.info(
                                'Sur la boîte : EXP / PER / PÉR = péremption ✅  ·  FAB / MFD / MFG = fabrication ❌. En cas de doute, reprenez la photo.',
                                { duration: 7000 }
                              )}
                              className="p-1 rounded text-zinc-400 hover:text-[#5B3B8C] hover:bg-purple-50"
                              title="Astuce : EXP / PER = péremption ✅  ·  FAB / MFD = fabrication ❌"
                              data-testid={`saisie-exp-help-${l.id}`}
                              aria-label="Aide péremption"
                            >
                              <HelpCircle className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                      {expBadge && (
                        <span
                          className={`mt-1 inline-block text-[10px] font-mono px-1.5 py-0.5 rounded border ${expBadge.cls}`}
                          title={expBadge.hint || ''}
                          data-testid={`saisie-exp-badge-${l.id}`}
                        >
                          {expBadge.label}
                        </span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono tabular-nums ${ecls}`}>{e === null ? '—' : (e > 0 ? '+' : '') + e}</td>
                    <td className={`px-3 py-2 text-right font-mono tabular-nums text-xs ${ecls}`}>{l.valeur_ecart_fcfa === null || l.valeur_ecart_fcfa === undefined ? '—' : fcfa(l.valeur_ecart_fcfa)}</td>
                    <td className="px-3 py-2">
                      {e !== null && e !== 0 && !l.discrepancy_id && !isClosed && canEditEcarts && (
                        <Button size="sm" variant="outline" onClick={() => setEcartLine(l)} className="border-red-300 text-red-700 hover:bg-red-50 h-7 text-xs" data-testid={`saisie-treat-${l.id}`}>
                          Traiter écart
                        </Button>
                      )}
                      {e !== null && e !== 0 && !l.discrepancy_id && !isClosed && !canEditEcarts && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                          {phaseOp2Active ? `En attente ${inv.op2_name}` : 'À traiter (phase OP2)'}
                        </Badge>
                      )}
                      {/* Legacy: ancien inventaire en_cours — permettre le traitement */}
                      {e !== null && e !== 0 && !l.discrepancy_id && status === 'en_cours' && canManagePhases && (
                        <Button size="sm" variant="outline" onClick={() => setEcartLine(l)} className="border-red-300 text-red-700 hover:bg-red-50 h-7 text-xs" data-testid={`saisie-treat-${l.id}`}>
                          Traiter écart
                        </Button>
                      )}
                      {l.discrepancy_id && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Traité</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!lines.length && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500">Aucun produit dans cette zone</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Phase 2/3 — Card Référent / Validation Manager ── */}
      {phaseReviewActive && (
        <InventoryReferentValidationCard
          inventory={inv}
          lines={data.lines || []}
          currentUser={currentUser}
          canManagePhases={canManagePhases}
          onTransition={() => load && load()}
        />
      )}

      {!isClosed && !phaseReviewActive && (
        <div className="fixed bottom-[70px] left-0 right-0 bg-white border-t border-zinc-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] p-3 z-[55]" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 flex-wrap">
            <div className="text-sm">
              <span className={nonSaisis > 0 ? 'text-red-700 font-semibold' : 'text-emerald-700 font-semibold'}>
                {saisis}/{totalLines} saisis
              </span>
              {nonSaisis > 0 && <span className="text-red-700"> · {nonSaisis} non saisi(s)</span>}
              <span className="text-zinc-500"> · </span>
              <span className={`font-mono font-semibold ${ecartTotal < 0 ? 'text-red-700' : ecartTotal > 0 ? 'text-[#C45C26]' : 'text-emerald-700'}`}>
                Écart : {fcfa(ecartTotal)}
              </span>
              {pendingEcarts > 0 && <span className="text-red-700 ml-2">· {pendingEcarts} écart(s) à traiter</span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Bouton « Mettre les non saisis à 0 » : utile uniquement pendant la phase OP1 */}
              {phaseOp1Active && canActAsOp1 && nonSaisis > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fillAllEmptyToZero}
                  className="border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                  data-testid="saisie-fill-zero-btn"
                  title="Met toutes les lignes non saisies à 0 (produits absents)"
                >
                  Mettre les {nonSaisis} non saisi(s) à 0
                </Button>
              )}

              {/* ── PHASE 1 : OP1 ferme son comptage ── */}
              {phaseOp1Active && canActAsOp1 && (
                <Button
                  size="sm"
                  onClick={closeOp1}
                  disabled={nonSaisis > 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                  data-testid="phase-close-op1-btn"
                  title={nonSaisis > 0 ? `${nonSaisis} ligne(s) non saisie(s)` : 'Fermer mon comptage — OP2 prendra la suite'}
                >
                  Fermer mon comptage (OP1)
                </Button>
              )}
              {phaseOp1Active && !canActAsOp1 && (
                <span className="text-xs text-zinc-500 italic">
                  En attente que <strong>{inv.op1_name}</strong> ferme son comptage
                </span>
              )}

              {/* ── PHASE INTERMÉDIAIRE : OP2 doit ouvrir son job ── */}
              {waitingOp2 && canActAsOp2 && (
                <Button
                  size="sm"
                  onClick={openOp2}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="phase-open-op2-btn"
                  title="Ouvrir le traitement des écarts"
                >
                  Ouvrir mon job (OP2 · traitement écarts)
                </Button>
              )}
              {waitingOp2 && !canActAsOp2 && (
                <span className="text-xs text-blue-700 italic">
                  En attente que <strong>{inv.op2_name}</strong> ouvre son job
                </span>
              )}

              {/* ── PHASE 2 : OP2 ferme son traitement ── */}
              {phaseOp2Active && canActAsOp2 && (
                <Button
                  size="sm"
                  onClick={closeOp2}
                  disabled={pendingEcarts > 0}
                  className="bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
                  data-testid="phase-close-op2-btn"
                  title={pendingEcarts > 0 ? `${pendingEcarts} écart(s) à traiter` : 'Fermer le traitement — Responsable clôturera'}
                >
                  Fermer mon job (OP2)
                </Button>
              )}
              {phaseOp2Active && !canActAsOp2 && (
                <span className="text-xs text-orange-700 italic">
                  En attente que <strong>{inv.op2_name}</strong> ferme le traitement
                </span>
              )}

              {/* ── PHASE 3 : Responsable clôture finale ── */}
              {phaseRespActive && canActAsResp && (
                <Button
                  size="sm"
                  onClick={closeInventory}
                  className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white"
                  data-testid="phase-close-final-btn"
                  title="Clôture définitive de l'inventaire"
                >
                  Clôturer l'inventaire (Responsable)
                </Button>
              )}
              {phaseRespActive && !canActAsResp && (
                <span className="text-xs text-emerald-700 italic">
                  En attente que <strong>{inv.responsable_name}</strong> clôture
                </span>
              )}

              {/* Legacy : ancien inventaire en_cours (un seul bouton, manager/admin) */}
              {status === 'en_cours' && canManagePhases && (
                <Button
                  size="sm"
                  onClick={closeInventory}
                  disabled={pendingEcarts > 0 || nonSaisis > 0}
                  className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white disabled:opacity-50"
                  data-testid="saisie-close-btn"
                  title="Inventaire en mode legacy — clôture directe (manager/admin)"
                >
                  Clôturer l'inventaire
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {ecartLine && (
        <DiscrepancyDialog
          line={ecartLine}
          agents={agents}
          enums={enums}
          inventoryId={inventoryId}
          onClose={() => setEcartLine(null)}
          onDone={() => { setEcartLine(null); load(); }}
        />
      )}

      {ocrLineId && (
        <ExpirationOcrDialog
          onClose={() => setOcrLineId(null)}
          onDetect={(iso) => {
            if (iso) submitExpirationDate(ocrLineId, iso);
            setOcrLineId(null);
          }}
        />
      )}

      {voiceLineId && (
        <VoiceSaisieDialog
          line={data?.lines?.find(l => l.id === voiceLineId)}
          onClose={() => setVoiceLineId(null)}
          onApply={(values) => applyVoiceCommand(voiceLineId, values)}
        />
      )}

      {lotsLineId && (
        <LotsDialog
          line={data?.lines?.find(l => l.id === lotsLineId)}
          onClose={() => setLotsLineId(null)}
          onSave={(payload) => submitLots(lotsLineId, payload)}
        />
      )}
      <InventoryStockAuditDialog
        open={auditOpen}
        inventoryId={inventoryId}
        zoneName={inv?.zone_name}
        onClose={() => setAuditOpen(false)}
        onRefreshed={load}
      />
    </div>
  );
};

const DiscrepancyDialog = ({ line, agents, enums, inventoryId, onClose, onDone }) => {
  const [step, setStep] = useState(1);
  const [recompte, setRecompte] = useState('');
  const [cause, setCause] = useState('');
  const [localisation, setLocalisation] = useState('');
  const [actionDesc, setActionDesc] = useState('');
  const [actionResp, setActionResp] = useState('');
  const [actionDate, setActionDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 2); return d.toISOString().slice(0, 10);
  });
  const [evidencePhotoUrl, setEvidencePhotoUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const next = () => setStep(s => Math.min(4, s + 1));
  const prev = () => setStep(s => Math.max(1, s - 1));

  const stepValid = step === 1 ? recompte !== '' : step === 2 ? cause !== '' : step === 3 ? localisation !== '' : actionDesc.trim() && actionResp && actionDate;

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/inventory/inventories/${inventoryId}/lines/${line.id}/discrepancy`,
        {
          recompte: parseInt(recompte, 10),
          cause, localisation,
          action_description: actionDesc.trim(),
          action_responsable_id: actionResp,
          action_date_limite: actionDate,
          evidence_photo_url: evidencePhotoUrl,
        },
        { headers: auth() }
      );
      toast.success("Écart enregistré + action corrective créée");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col gap-2 p-4 sm:p-6" data-testid="discrepancy-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-serif pr-6">Traiter un écart — étape {step}/4</DialogTitle>
        </DialogHeader>
        <div className="shrink-0 flex items-center gap-1">
          {[1, 2, 3, 4].map(s => <div key={s} className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-[#1B4D3E]' : 'bg-zinc-200'}`} />)}
        </div>
        <div className="shrink-0 text-xs text-zinc-500">{line.product_name} · théo {line.stock_theorique} · physique {line.stock_physique} · écart {line.ecart > 0 ? '+' : ''}{line.ecart}</div>

        <div className="flex-1 overflow-y-auto min-h-0 -mx-1 px-1">
        {step === 1 && (
          <div className="space-y-2">
            <p className="text-sm text-zinc-600">Recomptage par opérateur 2 :</p>
            <Input type="number" inputMode="numeric" value={recompte} onChange={(e) => setRecompte(e.target.value)} placeholder="Quantité réelle recomptée" className="font-mono text-lg" data-testid="discrepancy-recompte" />
          </div>
        )}
        {step === 2 && (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            <p className="text-sm text-zinc-600 mb-2">Cause probable :</p>
            {(enums?.causes || []).map(c => (
              <label key={c} className={`block p-2.5 rounded-md border cursor-pointer text-sm ${cause === c ? 'border-[#1B4D3E] bg-[#1B4D3E]/5' : 'border-zinc-200 hover:bg-zinc-50'}`}>
                <input type="radio" className="mr-2" checked={cause === c} onChange={() => setCause(c)} />{c}
              </label>
            ))}
          </div>
        )}
        {step === 3 && (
          <div className="grid grid-cols-2 gap-2">
            {(enums?.localisations || []).map(l => (
              <button key={l} type="button" onClick={() => setLocalisation(l)}
                      className={`p-2.5 rounded-md border text-left text-sm ${localisation === l ? 'border-[#1B4D3E] bg-[#1B4D3E]/5 text-[#1B4D3E] font-medium' : 'border-zinc-200 hover:bg-zinc-50'}`}>
                {l}
              </button>
            ))}
          </div>
        )}
        {step === 4 && (
          <div className="space-y-3">
            <Input value={actionDesc} onChange={(e) => setActionDesc(e.target.value)} placeholder="Action corrective (ex : Reclasser dans Frigo)" data-testid="discrepancy-action-desc" />
            <div className="grid grid-cols-2 gap-2">
              <select value={actionResp} onChange={(e) => setActionResp(e.target.value)} className="border border-zinc-300 rounded-md p-2 text-sm" data-testid="discrepancy-action-resp">
                <option value="">— Responsable —</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <Input type="date" value={actionDate} onChange={(e) => setActionDate(e.target.value)} />
            </div>
            <div className="border-t border-zinc-200 pt-3">
              <p className="text-sm text-zinc-600 mb-2">
                Photo justificative <span className="text-zinc-400">(facultative — recommandée pour les écarts importants)</span>
              </p>
              <EvidencePhotoUploader
                photoUrl={evidencePhotoUrl}
                onUploaded={setEvidencePhotoUrl}
              />
            </div>
          </div>
        )}
        </div>

        <DialogFooter className="shrink-0 flex-row justify-between border-t border-zinc-200 pt-3 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-white">
          <Button variant="outline" onClick={step === 1 ? onClose : prev}>{step === 1 ? 'Annuler' : 'Précédent'}</Button>
          {step < 4 ? (
            <Button onClick={next} disabled={!stepValid} className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white">Suivant</Button>
          ) : (
            <Button onClick={submit} disabled={!stepValid || submitting} className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white" data-testid="discrepancy-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────
// Actions correctives
// ─────────────────────────────────────────────────────────
const ActionsView = ({ actions, onUpdateStatus, onWhatsApp, reloading }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [filterStatut, setFilterStatut] = useState('open'); // open | all | done
  const [filterResp, setFilterResp] = useState('');

  // Personnes en charge uniques (pour le filtre)
  const responsables = useMemo(() => {
    const s = new Set();
    (actions || []).forEach(a => { if (a.responsable_name) s.add(a.responsable_name); });
    return Array.from(s).sort();
  }, [actions]);

  const filteredActions = useMemo(() => {
    return (actions || []).filter(a => {
      if (filterStatut === 'open' && !['a_faire', 'en_cours'].includes(a.statut)) return false;
      if (filterStatut === 'done' && !['corrige', 'non_corrige'].includes(a.statut)) return false;
      if (filterResp && a.responsable_name !== filterResp) return false;
      return true;
    });
  }, [actions, filterStatut, filterResp]);

  const fmtMaj = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };
  const fmtFcfa = (v) => {
    if (v === null || v === undefined) return '—';
    const sign = v < 0 ? '-' : '';
    return sign + Math.abs(v).toLocaleString('fr-FR').replace(/\u202f/g, ' ') + ' FCFA';
  };

  return (
    <div className="space-y-4" data-testid="inventaire-actions">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-serif font-semibold text-zinc-900">
            Produits à traiter — Actions correctives
          </h2>
          <p className="text-sm text-zinc-500">
            Vue synthétique des produits avec écart en attente de correction · Cliquez sur le statut ou 💬 pour notifier
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 font-semibold" data-testid="actions-counter-open">
            {(actions || []).filter(a => ['a_faire', 'en_cours'].includes(a.statut)).length} en attente
          </span>
          <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-900 font-semibold" data-testid="actions-counter-overdue">
            {(actions || []).filter(a => a.is_overdue).length} en retard
          </span>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="actions-filters">
        <div className="flex items-center gap-1.5 bg-white border border-zinc-200 rounded-md p-0.5">
          {[
            { id: 'open', label: 'À traiter' },
            { id: 'done', label: 'Traités' },
            { id: 'all', label: 'Tous' },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setFilterStatut(opt.id)}
              className={`px-2.5 py-1 text-xs rounded ${
                filterStatut === opt.id ? 'bg-[#1B4D3E] text-white font-medium' : 'text-zinc-600 hover:bg-zinc-100'
              }`}
              data-testid={`actions-filter-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <select
          value={filterResp}
          onChange={(e) => setFilterResp(e.target.value)}
          className="text-xs border border-zinc-200 rounded-md px-2 py-1.5 bg-white"
          data-testid="actions-filter-resp"
        >
          <option value="">Tous les responsables</option>
          {responsables.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <span className="text-xs text-zinc-500 ml-auto">
          {filteredActions.length} ligne(s) affichée(s)
        </span>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-3 py-2 font-medium">Produit / Rayon</th>
                <th className="px-3 py-2 font-medium">Écart constaté</th>
                <th className="px-3 py-2 font-medium">Action attendue</th>
                <th className="px-3 py-2 font-medium">Personne en charge</th>
                <th className="px-3 py-2 font-medium">Date limite</th>
                <th className="px-3 py-2 font-medium">Statut</th>
                <th className="px-3 py-2 font-medium">Dernière MAJ</th>
                <th className="px-3 py-2 font-medium text-right">Notifier</th>
              </tr>
            </thead>
            <tbody>
              {filteredActions.map(a => {
                const overdue = a.is_overdue || (a.statut === 'a_faire' && a.date_limite && a.date_limite < today);
                return (
                  <tr key={a.id} className={`border-b border-zinc-100 ${overdue ? 'bg-red-50' : ''}`} data-testid={`action-row-${a.id}`}>
                    <td className="px-3 py-2">
                      <div className="text-zinc-900 font-medium leading-tight">{a.product_name || '—'}</div>
                      <div className="text-[11px] text-zinc-500">{a.zone_name || '—'}</div>
                    </td>
                    <td className="px-3 py-2">
                      {(a.ecart_qty !== null && a.ecart_qty !== undefined) ? (
                        <>
                          <div className={`font-mono font-semibold ${a.ecart_qty < 0 ? 'text-red-700' : 'text-amber-700'}`}>
                            {a.ecart_qty > 0 ? '+' : ''}{a.ecart_qty} unité(s)
                          </div>
                          <div className="text-[11px] text-zinc-500 font-mono">{fmtFcfa(a.ecart_valeur_fcfa)}</div>
                          {a.ecart_cause && <div className="text-[11px] text-zinc-600 italic mt-0.5">{a.ecart_cause}</div>}
                        </>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-900">{a.description}</td>
                    <td className="px-3 py-2 text-zinc-700">{a.responsable_name || '—'}</td>
                    <td className={`px-3 py-2 font-mono whitespace-nowrap ${overdue ? 'text-red-700 font-semibold' : 'text-zinc-700'}`}>
                      {a.date_limite}{overdue && ' ⚠'}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={a.statut}
                        onChange={(e) => onUpdateStatus(a.id, e.target.value)}
                        className="border border-zinc-300 rounded-md text-xs p-1"
                        data-testid={`action-status-${a.id}`}
                      >
                        <option value="a_faire">À faire</option>
                        <option value="en_cours">En cours</option>
                        <option value="corrige">Corrigé</option>
                        <option value="non_corrige">Non corrigé</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-600 whitespace-nowrap font-mono">
                      {fmtMaj(a.updated_at)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => onWhatsApp(a)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`action-whatsapp-${a.id}`}>
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {!filteredActions.length && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500">
                  {reloading ? 'Chargement…' : 'Aucun produit à traiter avec ces filtres'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────
const SettingsView = ({ zones, agents, onUpdateZone, onCreateZone, onDeleteZone, onUpdateAgent, settings, onUpdateSettings, onUpdateAvailability, onToggleExcludeOp, emplacements, onSaveEmplacements, onAutoMapAll, onImportRegistre }) => {
  const [newZone, setNewZone] = useState('');
  const [editingZone, setEditingZone] = useState(null);
  const [empDialogZone, setEmpDialogZone] = useState(null);
  return (
    <div className="space-y-6" data-testid="inventaire-settings">
      <div>
        <h2 className="text-xl font-serif font-semibold text-zinc-900">Paramètres</h2>
        <p className="text-sm text-zinc-500">Zones, responsables, agents et seuils</p>
      </div>

      {/* Settings */}
      <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-3">
        <h3 className="text-sm font-semibold text-zinc-900">Seuil pharmacien</h3>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            value={settings?.ecart_seuil_pharmacien_fcfa ?? ''}
            onChange={(e) => onUpdateSettings({ ecart_seuil_pharmacien_fcfa: parseInt(e.target.value, 10) || 0 })}
            className="w-40 font-mono"
            data-testid="settings-seuil"
          />
          <span className="text-sm text-zinc-600">FCFA · alerte au pharmacien si écart supérieur</span>
        </div>
      </div>

      {/* Zones with responsables */}
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-zinc-900">Rayons & responsables ({zones.length})</h3>
          <div className="flex gap-2 flex-wrap">
            <label
              className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-[#1B4D3E] text-[#1B4D3E] rounded-md hover:bg-emerald-50 transition"
              data-testid="import-registre-label"
            >
              📊 Importer le registre Excel
              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onImportRegistre(f);
                  e.target.value = '';
                }}
                data-testid="import-registre-input"
              />
            </label>
            <Button
              size="sm"
              variant="outline"
              onClick={onAutoMapAll}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="auto-map-btn"
              title="Suggestion par mots-clés (utiliser si vous n'avez pas de fichier registre)"
            >
              ✨ Auto-map (mots-clés)
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-2 py-2 font-medium">Rayon</th>
                <th className="px-2 py-2 font-medium">Type</th>
                <th className="px-2 py-2 font-medium">Responsable</th>
                <th className="px-2 py-2 font-medium">Emplacements Mediciel</th>
                <th className="px-2 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {zones.map(z => (
                <tr key={z.id} className="border-b border-zinc-100">
                  <td className="px-2 py-1.5">
                    {editingZone === z.id ? (
                      <Input
                        defaultValue={z.name}
                        autoFocus
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && v !== z.name) onUpdateZone(z.id, { ...z, name: v });
                          setEditingZone(null);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingZone(null); }}
                        className="h-8 text-sm"
                        data-testid={`zone-edit-input-${z.id}`}
                      />
                    ) : (
                      <button onClick={() => setEditingZone(z.id)} className="text-left text-zinc-900 hover:text-[#1B4D3E] font-medium">
                        {z.name}
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <button
                      onClick={() => onUpdateZone(z.id, { ...z, is_critical: !z.is_critical })}
                      className={`text-xs px-2 py-1 rounded-md border ${z.is_critical ? 'bg-orange-50 border-orange-200 text-[#C45C26]' : 'bg-zinc-50 border-zinc-200 text-zinc-600'}`}
                      data-testid={`zone-toggle-${z.id}`}
                      title="Cliquer pour basculer"
                    >
                      {z.is_critical ? 'Critique' : 'Standard'}
                    </button>
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      value={z.default_responsable_id || ''}
                      onChange={(e) => onUpdateZone(z.id, { ...z, default_responsable_id: e.target.value || null })}
                      className="border border-zinc-300 rounded-md text-sm p-1"
                      data-testid={`zone-resp-${z.id}`}
                    >
                      <option value="">— Aucun —</option>
                      {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => setEmpDialogZone(z)}
                        className={`text-xs px-2 py-1 rounded-md border transition ${
                          (z.mediciel_emplacement_ids || []).length === 0
                            ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                            : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                        }`}
                        data-testid={`zone-emp-btn-${z.id}`}
                        title="Mapper les emplacements Mediciel"
                      >
                        {(z.mediciel_emplacement_ids || []).length === 0
                          ? '⚠ À mapper'
                          : `${(z.mediciel_emplacement_ids || []).length} emplacement(s)`}
                      </button>
                      {z.product_name_range && (z.product_name_range.min || z.product_name_range.max_exclusive) && (
                        <span
                          className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-purple-50 border border-purple-200 text-purple-700"
                          title="Filtre alphabétique sur le nom du produit"
                          data-testid={`zone-range-${z.id}`}
                        >
                          {(() => {
                            const minC = (z.product_name_range.min || 'A').toUpperCase();
                            const maxEx = z.product_name_range.max_exclusive;
                            let maxC = 'Z';
                            if (maxEx) {
                              const code = maxEx.toUpperCase().charCodeAt(0) - 1;
                              // Clamp to A-Z range (handles sentinel '{' which becomes Z)
                              maxC = String.fromCharCode(Math.min(90, Math.max(65, code)));
                            }
                            return `· ${minC}–${maxC}`;
                          })()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => onDeleteZone(z.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); if (newZone.trim()) { onCreateZone(newZone.trim()); setNewZone(''); } }} className="flex gap-2 mt-3">
          <Input placeholder="Nouveau rayon…" value={newZone} onChange={(e) => setNewZone(e.target.value)} data-testid="zone-new-input" />
          <Button type="submit" className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white" data-testid="zone-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </form>
        <p className="text-xs text-zinc-500 mt-2">💡 Cliquer sur un nom de rayon pour le renommer. Le bouton « Emplacements Mediciel » est obligatoire pour démarrer un inventaire (sinon les ~7 000 produits seraient chargés).</p>
      </div>

      {empDialogZone && (
        <EmplacementsMappingDialog
          zone={empDialogZone}
          emplacements={emplacements || []}
          onClose={() => setEmpDialogZone(null)}
          onSave={onSaveEmplacements}
        />
      )}

      {/* Disponibilités agents (jours de repos + congés) */}
      <AvailabilityCard agents={agents} onUpdateAvailability={onUpdateAvailability} onToggleExcludeOp={onToggleExcludeOp} />

      {/* Permissions onglets Inventaire (admin only) */}
      <PermissionsCard />

      {/* Agents WhatsApp + Email */}
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-zinc-900 mb-3">WhatsApp & Email des agents</h3>
        <p className="text-xs text-zinc-500 mb-3">
          WhatsApp : format 0708XXXXXX (préfixe 225 ajouté auto). Email : pour
          recevoir les récaps PDF de fin de cycle.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-2 py-2 font-medium">Nom</th>
                <th className="px-2 py-2 font-medium">Téléphone</th>
                <th className="px-2 py-2 font-medium">WhatsApp</th>
                <th className="px-2 py-2 font-medium">Email</th>
              </tr>
            </thead>
            <tbody>
              {agents.map(a => (
                <tr key={a.id} className="border-b border-zinc-100">
                  <td className="px-2 py-1.5 text-zinc-900">{a.name}</td>
                  <td className="px-2 py-1.5 font-mono text-xs text-zinc-500">{a.phone}</td>
                  <td className="px-2 py-1.5">
                    <Input
                      defaultValue={a.whatsapp_phone || ''}
                      placeholder={a.phone}
                      className="h-8 text-sm font-mono"
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== (a.whatsapp_phone || '')) onUpdateAgent(a.id, { whatsapp_phone: v });
                      }}
                      data-testid={`agent-wa-${a.id}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      type="email"
                      defaultValue={a.email || ''}
                      placeholder="agent@exemple.com"
                      className="h-8 text-sm"
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== (a.email || '')) onUpdateAgent(a.id, { email: v });
                      }}
                      data-testid={`agent-email-${a.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Responsables de rayon (KPI + historique)
// ─────────────────────────────────────────────────────────
const scoreClass = (s) => {
  if (s >= 85) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (s >= 70) return 'bg-orange-100 text-[#C45C26] border-orange-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

const ResponsablesView = ({ rows, history, onReload, onSnapshot, onDownloadRecap, onSendRecap }) => {
  const [snapshotting, setSnapshotting] = useState(false);
  const [recapBusy, setRecapBusy] = useState(false);
  const periodHistory = (history || []).slice(0, 6);
  const safeRows = rows || [];

  const promptRecap = (mode) => async () => {
    const today = new Date().toISOString().slice(0, 10);
    const sixWeeksAgo = new Date(Date.now() - 42 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const from = window.prompt('Date de début (YYYY-MM-DD) :', sixWeeksAgo);
    if (!from) return;
    const to = window.prompt('Date de fin (YYYY-MM-DD) :', today);
    if (!to) return;
    setRecapBusy(true);
    try {
      if (mode === 'pdf') await onDownloadRecap(from, to);
      else await onSendRecap(from, to);
    } finally { setRecapBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="inventaire-responsables">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-serif font-semibold text-zinc-900">Responsables de rayon</h2>
          <p className="text-sm text-zinc-500">
            Score de fiabilité = 50% taux récupération · 30% délai correction · 20% densité d'écarts
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={onReload}>
            <RefreshCw className="w-4 h-4 mr-2" /> Recharger
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={recapBusy}
            onClick={promptRecap('pdf')}
            data-testid="recap-download-btn"
          >
            {recapBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-2" />}
            Récap PDF cycle
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            disabled={recapBusy}
            onClick={promptRecap('send')}
            data-testid="recap-send-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" /> Envoyer aux 6 agents
          </Button>
          <Button
            size="sm"
            className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white"
            disabled={snapshotting}
            onClick={async () => { setSnapshotting(true); try { await onSnapshot(); } finally { setSnapshotting(false); } }}
            data-testid="resp-snapshot-btn"
          >
            {snapshotting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-2" />}
            Snapshot mensuel
          </Button>
        </div>
      </div>

      {!safeRows.length ? (
        <div className="rounded-lg border border-zinc-200 bg-white p-6 text-center text-sm text-zinc-500">
          Aucun rayon n'a encore de responsable permanent assigné. Renseignez-les dans l'onglet Paramètres.
        </div>
      ) : (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-3 py-2 font-medium">Responsable</th>
                  <th className="px-3 py-2 font-medium">Rayons</th>
                  <th className="px-3 py-2 font-medium text-right">Inventaires</th>
                  <th className="px-3 py-2 font-medium text-right">Écarts</th>
                  <th className="px-3 py-2 font-medium text-right">Retrouvés</th>
                  <th className="px-3 py-2 font-medium text-right">Non retrouvés</th>
                  <th className="px-3 py-2 font-medium text-right">Valeur</th>
                  <th className="px-3 py-2 font-medium text-right">Récup.</th>
                  <th className="px-3 py-2 font-medium text-right">Délai moy.</th>
                  <th className="px-3 py-2 font-medium text-right">Score</th>
                </tr>
              </thead>
              <tbody>
                {safeRows.map((r) => (
                  <tr key={r.responsable_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`resp-row-${r.responsable_id}`}>
                    <td className="px-3 py-2 font-medium text-zinc-900">{r.responsable_name || '(inconnu)'}</td>
                    <td className="px-3 py-2 text-zinc-700 text-xs max-w-[260px]">
                      <span className="line-clamp-2" title={(r.zones || []).join(' · ')}>
                        {(r.zones || []).join(' · ')}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-right tabular-nums">{r.nb_inventaires}</td>
                    <td className="px-3 py-2 font-mono text-right tabular-nums">{r.nb_ecarts}</td>
                    <td className="px-3 py-2 font-mono text-right tabular-nums text-emerald-700">{r.nb_retrouves}</td>
                    <td className="px-3 py-2 font-mono text-right tabular-nums text-red-700">{r.nb_non_retrouves}</td>
                    <td className="px-3 py-2 font-mono text-right tabular-nums">{fcfa(r.valeur_ecarts_fcfa)}</td>
                    <td className="px-3 py-2 font-mono text-right tabular-nums">{r.taux_recuperation}%</td>
                    <td className="px-3 py-2 font-mono text-right tabular-nums">{r.delai_moyen_correction_h ? `${r.delai_moyen_correction_h} h` : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Badge className={`${scoreClass(r.score_fiabilite)} border font-mono`}>{r.score_fiabilite}/100</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {periodHistory.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-zinc-900 mb-3">Évolution du score (derniers mois)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-3 py-2 font-medium">Responsable</th>
                  {periodHistory.map(h => (
                    <th key={h.period} className="px-3 py-2 font-medium text-right font-mono">{h.period}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {safeRows.map(r => (
                  <tr key={r.responsable_id} className="border-b border-zinc-100">
                    <td className="px-3 py-2 text-zinc-900">{r.responsable_name}</td>
                    {periodHistory.map(h => {
                      const past = (h.rows || []).find(x => x.responsable_id === r.responsable_id);
                      return (
                        <td key={h.period} className="px-3 py-2 text-right font-mono tabular-nums">
                          {past ? `${past.score_fiabilite}` : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            Un snapshot par période est conservé. Cliquez « Snapshot mensuel » à chaque clôture de mois.
          </p>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Modale mapping rayon ↔ emplacements Mediciel
// ─────────────────────────────────────────────────────────
const EmplacementsMappingDialog = ({ zone, emplacements, onClose, onSave }) => {
  const [selected, setSelected] = useState(new Set(zone.mediciel_emplacement_ids || []));
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredEmp, setFilteredEmp] = useState(null); // null = use props, [] = empty
  const [searching, setSearching] = useState(false);

  // Recherche serveur avec debounce 350ms : balaie TOUS les emplacements (pas
  // seulement le sample des 5 produits). Indispensable pour TABLEAU/STUPÉFIANTS
  // dont les produits ne sont pas dans le sample par défaut.
  useEffect(() => {
    const q = search.trim();
    if (!q) { setFilteredEmp(null); return; }
    setSearching(true);
    const tok = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API}/inventory/mediciel-emplacements?search=${encodeURIComponent(q)}`,
          { headers: auth() },
        );
        setFilteredEmp(r.data || []);
      } catch (e) {
        setFilteredEmp([]);
      } finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(tok);
  }, [search]);

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const save = async () => {
    setBusy(true);
    try { await onSave(zone.id, Array.from(selected).sort((a, b) => a - b)); onClose(); }
    finally { setBusy(false); }
  };

  const list = filteredEmp !== null ? filteredEmp : emplacements;
  const highlightMatch = (txt) => {
    const q = search.trim();
    if (!q) return txt;
    try {
      const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
      const parts = txt.split(re);
      return parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-amber-200 text-amber-900 px-0.5 rounded">{p}</mark>
          : <span key={i}>{p}</span>,
      );
    } catch { return txt; }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-3"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="emplacement-mapping-dialog"
      >
        <div className="shrink-0 flex items-center justify-between p-5 pb-3 border-b border-zinc-100">
          <div>
            <h3 className="text-lg font-serif font-semibold text-zinc-900">Emplacements Mediciel · {zone.name}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              Sélectionnez les emplacements correspondant à ce rayon. Tapez un nom de produit (ex.&nbsp;
              <em>« tramadol »</em>, <em>« morphine »</em>) pour trouver l'emplacement où il est rangé.
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-xl shrink-0">×</button>
        </div>

        {/* Barre de recherche */}
        <div className="shrink-0 px-5 pt-3 pb-2 border-b border-zinc-100">
          <div className="relative">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 Rechercher un produit (ex: tramadol, diazépam, codéine, morphine…)"
              className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1B4D3E] focus:ring-1 focus:ring-[#1B4D3E]"
              data-testid="emp-search-input"
            />
            {searching && (
              <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-zinc-400" />
            )}
          </div>
          {search.trim() && filteredEmp !== null && (
            <p className="text-[11px] text-zinc-500 mt-1.5">
              {filteredEmp.length === 0
                ? <span className="text-amber-700">Aucun emplacement ne contient un produit correspondant à « {search} ». Essayez un autre nom.</span>
                : <>{filteredEmp.length} emplacement(s) trouvé(s) — les produits matchants sont surlignés.</>}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-5 py-3">
          <div className="grid sm:grid-cols-2 gap-2">
            {list.map(e => {
              const isSel = selected.has(e.emplacement_id);
              const conflict = (e.mapped_to_zones || []).filter(n => n !== zone.name);
              return (
                <button
                  key={e.emplacement_id}
                  onClick={() => toggle(e.emplacement_id)}
                  className={`text-left rounded-lg border-2 p-2.5 transition ${
                    isSel ? 'border-[#1B4D3E] bg-emerald-50' : 'border-zinc-200 bg-white hover:border-zinc-300'
                  }`}
                  data-testid={`emp-toggle-${e.emplacement_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono font-bold text-zinc-900">ID {e.emplacement_id}</span>
                    <div className="flex items-center gap-2">
                      {e.matching_products_count > 0 && (
                        <span className="text-[10px] bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5 font-medium">
                          {e.matching_products_count} match
                        </span>
                      )}
                      <span className="text-xs text-zinc-500">{e.products_count} produits</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-600 line-clamp-3">
                    {(e.sample_products || []).slice(0, 4).map((n, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="text-zinc-300"> · </span>}
                        {highlightMatch(n)}
                      </React.Fragment>
                    ))}
                  </p>
                  {conflict.length > 0 && (
                    <p className="text-[10px] text-amber-700 mt-1">⚠ déjà mappé sur : {conflict.join(', ')}</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="shrink-0 flex justify-end gap-2 px-5 py-3 border-t border-zinc-200 bg-white">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={save} disabled={busy} className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white" data-testid="emp-save-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enregistrer ({selected.size})
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Disponibilités agents (jours de repos + mois de congés)
// ─────────────────────────────────────────────────────────
const WEEKDAYS = [
  { v: 0, label: 'Lundi' },
  { v: 1, label: 'Mardi' },
  { v: 2, label: 'Mercredi' },
  { v: 3, label: 'Jeudi' },
  { v: 4, label: 'Vendredi' },
  { v: 5, label: 'Samedi' },
  { v: 6, label: 'Dimanche' },
];
const MONTHS_FR = [
  'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc',
];
const KNOWN_AGENT_FIRST_NAMES = ['Bassira', 'Bénédicte', 'Benedicte', 'Boni', 'Cyrille', 'Landrie', 'Léonce', 'Leonce'];

const AvailabilityCard = ({ agents, onUpdateAvailability, onToggleExcludeOp }) => {
  // Restrict to the 6 procedure agents (match by first-name)
  const inventoryAgents = (agents || []).filter(a => {
    const first = (a.name || '').split(' ')[0].trim();
    return KNOWN_AGENT_FIRST_NAMES.some(k => first.toLowerCase() === k.toLowerCase());
  });

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="availability-card">
      <h3 className="text-sm font-semibold text-zinc-900 mb-1">Disponibilités agents</h3>
      <p className="text-xs text-zinc-500 mb-3">
        Jour de repos hebdomadaire + mois de congé annuel. Les agents absents ne sont jamais
        désignés op1/op2 sur leur jour ou leur mois. Si la zone d'un responsable tombe sur
        son repos, l'inventaire est conservé et il valide en différé. Vendredi saturé →
        repli automatique au samedi.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-2 py-2 font-medium">Agent</th>
              <th className="px-2 py-2 font-medium">Jour de repos</th>
              <th className="px-2 py-2 font-medium">Mois de congé</th>
              <th className="px-2 py-2 font-medium">Exclu OP1/OP2</th>
            </tr>
          </thead>
          <tbody>
            {inventoryAgents.map(a => (
              <AvailabilityRow
                key={a.id}
                agent={a}
                onUpdate={(rest, months) => onUpdateAvailability(a.id, rest, months)}
                onToggleExclude={(next) => onToggleExcludeOp && onToggleExcludeOp(a.id, a.name, next)}
              />
            ))}
            {!inventoryAgents.length && (
              <tr><td colSpan={4} className="px-2 py-4 text-center text-zinc-500 text-xs">
                Aucun agent reconnu. Vérifiez les prénoms (Bassira, Bénédicte, Boni, Cyrille, Landrie, Léonce).
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AvailabilityRow = ({ agent, onUpdate, onToggleExclude }) => {
  const [rest, setRest] = useState(
    agent.inventory_rest_day_of_week !== undefined && agent.inventory_rest_day_of_week !== null
      ? String(agent.inventory_rest_day_of_week) : ''
  );
  const [months, setMonths] = useState(agent.inventory_vacation_months || []);

  // Re-sync local state when parent reloads agent data
  useEffect(() => {
    setRest(
      agent.inventory_rest_day_of_week !== undefined && agent.inventory_rest_day_of_week !== null
        ? String(agent.inventory_rest_day_of_week) : ''
    );
    setMonths(agent.inventory_vacation_months || []);
  }, [agent.inventory_rest_day_of_week, agent.inventory_vacation_months]);

  const persist = (newRest, newMonths) => {
    const r = newRest === '' ? null : parseInt(newRest, 10);
    onUpdate(r, newMonths);
  };
  const toggleMonth = (m) => {
    const newSet = months.includes(m) ? months.filter(x => x !== m) : [...months, m].sort((a, b) => a - b);
    setMonths(newSet);
    persist(rest, newSet);
  };
  return (
    <tr className="border-b border-zinc-100" data-testid={`availability-row-${agent.id}`}>
      <td className="px-2 py-2 text-zinc-900 align-top whitespace-nowrap">{agent.name}</td>
      <td className="px-2 py-2 align-top">
        <select
          value={rest}
          onChange={(e) => { setRest(e.target.value); persist(e.target.value, months); }}
          className="border border-zinc-300 rounded-md text-sm p-1.5"
          data-testid={`availability-rest-${agent.id}`}
        >
          <option value="">— Aucun —</option>
          {WEEKDAYS.map(w => <option key={w.v} value={String(w.v)}>{w.label}</option>)}
        </select>
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap gap-1">
          {MONTHS_FR.map((mLabel, i) => {
            const m = i + 1;
            const active = months.includes(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => toggleMonth(m)}
                className={`text-xs px-2 py-1 rounded-md border transition ${
                  active
                    ? 'bg-[#1B4D3E] border-[#1B4D3E] text-white'
                    : 'bg-white border-zinc-300 text-zinc-600 hover:border-[#1B4D3E]'
                }`}
                data-testid={`availability-month-${agent.id}-${m}`}
              >
                {mLabel}
              </button>
            );
          })}
        </div>
      </td>
      <td className="px-2 py-2 align-middle">
        <label className="inline-flex items-center gap-2 cursor-pointer text-xs" title="Exclut l'agent du round-robin OP1/OP2 (reste responsable de rayon si configuré)">
          <input
            type="checkbox"
            checked={!!agent.inventory_exclude_op}
            onChange={(e) => onToggleExclude && onToggleExclude(e.target.checked)}
            className="w-4 h-4 accent-red-600"
            data-testid={`availability-exclude-${agent.id}`}
          />
          <span className={agent.inventory_exclude_op ? 'text-red-700 font-medium' : 'text-zinc-600'}>
            {agent.inventory_exclude_op ? 'Exclu' : 'Inclus'}
          </span>
        </label>
      </td>
    </tr>
  );
};

// ─────────────────────────────────────────────────────────
// Permissions onglets Inventaire (admin only)
// ─────────────────────────────────────────────────────────
const TAB_PERMS = [
  { id: 'dashboard', label: 'Tableau de bord' },
  { id: 'responsables', label: 'Responsables' },
  { id: 'actions', label: 'Actions correctives' },
  { id: 'settings', label: 'Paramètres' },
];

const PermissionsCard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users`, { headers: auth() });
      // Exclure les admins (ils ont déjà tout) et les clients (hors module)
      const filtered = (r.data || []).filter(u => !u.is_admin && u.role !== 'client');
      setUsers(filtered);
    } catch (e) {
      toast.error('Erreur chargement utilisateurs');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const togglePerm = async (userId, perm, granted) => {
    const u = users.find(x => x.id === userId);
    if (!u) return;
    const current = Array.isArray(u.inventory_tab_permissions) ? u.inventory_tab_permissions : [];
    const next = granted
      ? Array.from(new Set([...current, perm]))
      : current.filter(p => p !== perm);
    setSavingId(userId);
    try {
      await axios.put(
        `${API}/users/${userId}/inventory-tab-permissions`,
        { permissions: next },
        { headers: auth() }
      );
      setUsers(prev => prev.map(x =>
        x.id === userId ? { ...x, inventory_tab_permissions: next.sort() } : x
      ));
      toast.success(`${u.name} · ${granted ? 'accès accordé' : 'accès retiré'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur permission');
    } finally { setSavingId(null); }
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="permissions-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4 text-[#5B3B8C]" />
        <h3 className="text-sm font-semibold text-zinc-900">Permissions onglets Inventaire</h3>
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        L'onglet <strong>Planning</strong> est toujours accessible. Cochez les onglets supplémentaires
        à autoriser pour chaque agent / manager. Les administrateurs ont accès à tout par défaut.
      </p>

      {loading ? (
        <div className="text-sm text-zinc-500 py-4 text-center">
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Chargement…
        </div>
      ) : !users.length ? (
        <div className="text-sm text-zinc-500 py-4 text-center">Aucun utilisateur à configurer.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-2 py-2 font-medium">Utilisateur</th>
                <th className="px-2 py-2 font-medium">Rôle</th>
                {TAB_PERMS.map(t => (
                  <th key={t.id} className="px-2 py-2 font-medium text-center">{t.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const granted = Array.isArray(u.inventory_tab_permissions) ? u.inventory_tab_permissions : [];
                return (
                  <tr key={u.id} className="border-b border-zinc-100" data-testid={`perm-row-${u.id}`}>
                    <td className="px-2 py-1.5 text-zinc-900 whitespace-nowrap">{u.name}</td>
                    <td className="px-2 py-1.5 text-xs text-zinc-500">
                      <Badge variant="outline" className="font-mono text-[10px]">{u.role || 'agent'}</Badge>
                    </td>
                    {TAB_PERMS.map(t => {
                      const checked = granted.includes(t.id);
                      const busy = savingId === u.id;
                      return (
                        <td key={t.id} className="px-2 py-1.5 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={busy}
                            onChange={(e) => togglePerm(u.id, t.id, e.target.checked)}
                            className="w-4 h-4 accent-[#5B3B8C] cursor-pointer disabled:cursor-wait"
                            data-testid={`perm-${u.id}-${t.id}`}
                            title={`${checked ? 'Retirer' : 'Accorder'} l'accès à ${t.label}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


const TABS = [
  { id: 'dashboard', label: 'Tableau de bord', icon: TrendingUp },
  { id: 'planning', label: 'Planning', icon: CalendarDays },
  { id: 'pilotage', label: 'Pilotage', icon: Gauge, restricted: 'admin_or_manager' },
  { id: 'responsables', label: 'Responsables', icon: Users },
  { id: 'actions', label: 'Actions correctives', icon: AlertCircle },
  { id: 'settings', label: 'Paramètres', icon: SettingsIcon },
];

const InventoryGuideDialog = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-3"
      onClick={onClose}
      data-testid="inv-guide-dialog"
    >
      <div
        className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 bg-gradient-to-r from-[#5B3B8C] to-[#7C4FB8] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <BookOpen className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-semibold">Guide utilisateur — SADAStock</h2>
              <p className="text-xs text-white/80">Inventaire tournant avancé · Procédure V1.0</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10 text-white" data-testid="inv-guide-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5 space-y-5 text-sm text-zinc-700 leading-relaxed">
          <section>
            <h3 className="font-semibold text-zinc-900 mb-1.5 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-purple-100 text-[#5B3B8C] text-xs flex items-center justify-center font-bold">1</span>
              Préparer les paramètres (1 fois)
            </h3>
            <ul className="list-disc pl-9 space-y-1">
              <li>Onglet <b>Paramètres</b> → assigner un <b>responsable permanent</b> à chaque rayon (obligatoire pour générer le cycle).</li>
              <li>Importer le <b>Registre Excel</b> (.xlsx) pour mapper automatiquement les rayons aux emplacements Mediciel via les codes EAN. Les rayons volumineux comme « TABLEAU » sont scindés alphabétiquement (ex. <span className="font-mono bg-purple-50 text-purple-700 px-1 rounded">A–H</span> / <span className="font-mono bg-purple-50 text-purple-700 px-1 rounded">I–Z</span>).</li>
              <li>Pour chaque agent, configurer le <b>jour de repos</b> et les <b>mois de congé</b> (carte « Disponibilités »).</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-zinc-900 mb-1.5 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-purple-100 text-[#5B3B8C] text-xs flex items-center justify-center font-bold">2</span>
              Générer un cycle 6 semaines
            </h3>
            <ul className="list-disc pl-9 space-y-1">
              <li>Onglet <b>Planning</b> → bouton <b>« Générer cycle »</b>, choisir le <b>lundi de départ</b> et le nombre de cycles.</li>
              <li>Le système attribue op1 / op2 en round-robin <b>en respectant les disponibilités</b>.</li>
              <li>Aucune date ne tombe sur <b>samedi, dimanche ou jour férié</b> ivoirien — le planning est décalé automatiquement.</li>
              <li>Le responsable de rayon reste fixe (responsabilisation permanente).</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-zinc-900 mb-1.5 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-purple-100 text-[#5B3B8C] text-xs flex items-center justify-center font-bold">3</span>
              Saisir un inventaire (mobile / tablette)
            </h3>
            <ul className="list-disc pl-9 space-y-1">
              <li>Onglet <b>Planning</b> → bouton <b>« Saisir »</b> sur la ligne du jour.</li>
              <li>Saisir le <b>stock physique</b> rayon + réserve. Le stock théorique est récupéré en temps réel via Mediciel.</li>
              <li><b>Scanner code-barres</b> 📷 : bouton « Scanner » en haut à droite → vise l'EAN du produit → la ligne est automatiquement sélectionnée et focalisée (gain de temps massif sur les rayons denses).</li>
              <li><b>Date de péremption</b> 📅 : colonne dédiée dans la table — saisie manuelle ou via OCR caméra (icône 📷 à côté de l'input). Badges automatiques : <span className="text-red-700 font-medium">Périmé</span> / <span className="text-orange-700 font-medium">&lt;3 mois</span> / <span className="text-amber-700 font-medium">&lt;6 mois</span>.</li>
              <li>Le système détecte les écarts ; pour chaque écart il faut <b>recompter</b>, indiquer la <b>cause</b>, la <b>localisation</b> et joindre une <b>photo justificative</b> (facultative mais recommandée).</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-zinc-900 mb-1.5 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-purple-100 text-[#5B3B8C] text-xs flex items-center justify-center font-bold">4</span>
              Gérer les écarts &amp; corriger
            </h3>
            <ul className="list-disc pl-9 space-y-1">
              <li>Onglet <b>Actions correctives</b> → assigner, suivre le statut (à faire / en cours / fait).</li>
              <li>Bouton <b>WhatsApp</b> : envoie un message gratuit (lien <span className="font-mono bg-zinc-100 px-1 rounded">wa.me</span>) à l'agent concerné.</li>
              <li>Bouton <b>Excel correction Mediciel</b> sur l'inventaire clôturé : génère le fichier de correction prêt à l'emploi (Code EAN · Quantité · Sens ENTRÉE/SORTIE).</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-zinc-900 mb-1.5 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-purple-100 text-[#5B3B8C] text-xs flex items-center justify-center font-bold">5</span>
              Suivi de la performance
            </h3>
            <ul className="list-disc pl-9 space-y-1">
              <li>Onglet <b>Tableau de bord</b> → KPIs globaux (taux de récupération, valeur des écarts).</li>
              <li>Onglet <b>Responsables</b> → score de fiabilité par responsable de rayon, historique mensuel, <b>PDF récap fin de cycle</b> envoyé par email + WhatsApp.</li>
            </ul>
          </section>

          <section className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <h3 className="font-semibold text-amber-900 mb-1 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Bon à savoir
            </h3>
            <ul className="list-disc pl-7 space-y-0.5 text-amber-900 text-xs">
              <li>Si un rayon n'a pas d'emplacements Mediciel mappés, l'inventaire ne peut pas démarrer (sécurité anti-chargement de 7 000 produits).</li>
              <li>Les agents en repos / congé ne sont jamais désignés sur leur jour d'absence.</li>
              <li>Les responsables absents le jour de leur inventaire valideront en différé (badge « différé » visible dans le planning).</li>
            </ul>
          </section>
        </div>

        <div className="shrink-0 bg-white border-t border-zinc-200 px-6 py-3 flex justify-end">
          <Button onClick={onClose} className="bg-[#5B3B8C] hover:bg-[#4A2F73] text-white" data-testid="inv-guide-ok">
            J'ai compris
          </Button>
        </div>
      </div>
    </div>
  );
};


const InventoryTab = () => {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = !!(user?.is_admin) || role === 'admin';
  const isManager = role === 'manager';
  const isPrivileged = isAdmin;  // Seul l'admin a accès complet par défaut
  // Permissions par onglet (planning toujours autorisé). L'admin peut accorder
  // des onglets supplémentaires à n'importe quel utilisateur via Paramètres → Permissions inventaire.
  const grantedTabs = Array.isArray(user?.inventory_tab_permissions) ? user.inventory_tab_permissions : [];
  const canSeeTab = (tabId) => {
    if (tabId === 'planning') return true;        // toujours accessible
    if (tabId === 'pilotage') return isAdmin || isManager;  // admin + manager uniquement
    if (isAdmin) return true;                     // admin = tout
    return grantedTabs.includes(tabId);
  };
  // Si l'onglet par défaut n'est pas accessible, ouvrir Planning
  const defaultTab = isAdmin ? 'dashboard' : 'planning';
  const [tab, setTab] = useState(defaultTab);
  const [showGuide, setShowGuide] = useState(false);
  const [zones, setZones] = useState([]);
  const [agents, setAgents] = useState([]);
  const [plannings, setPlannings] = useState([]);
  const [actions, setActions] = useState([]);
  const [enums, setEnums] = useState({ causes: [], localisations: [] });
  const [kpi, setKpi] = useState(null);
  const [unassigned, setUnassigned] = useState({ count: 0, zones: [] });
  const [respKpi, setRespKpi] = useState([]);
  const [respHistory, setRespHistory] = useState([]);
  const [emplacements, setEmplacements] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activeInventoryId, setActiveInventoryId] = useState(null);
  const [reloading, setReloading] = useState(false);

  const reload = useCallback(async () => {
    setReloading(true);
    try {
      const [z, a, p, ac, en, k, s, u, rk, rh, emp] = await Promise.all([
        axios.get(`${API}/inventory/zones`, { headers: auth() }),
        axios.get(`${API}/inventory/agents`, { headers: auth() }),
        axios.get(`${API}/inventory/plannings`, { headers: auth() }),
        axios.get(`${API}/inventory/actions`, { headers: auth() }),
        axios.get(`${API}/inventory/enums`, { headers: auth() }),
        axios.get(`${API}/inventory/dashboard`, { headers: auth() }),
        axios.get(`${API}/inventory/settings`, { headers: auth() }),
        axios.get(`${API}/inventory/zones/unassigned`, { headers: auth() }),
        axios.get(`${API}/inventory/kpi/responsables`, { headers: auth() }),
        axios.get(`${API}/inventory/kpi/responsables/history?months=6`, { headers: auth() }),
        axios.get(`${API}/inventory/mediciel-emplacements`, { headers: auth() }),
      ]);
      setZones(z.data); setAgents(a.data); setPlannings(p.data);
      setActions(ac.data); setEnums(en.data); setKpi(k.data); setSettings(s.data);
      setUnassigned(u.data); setRespKpi(rk.data); setRespHistory(rh.data);
      setEmplacements(emp.data);
    } catch (e) { toast.error('Erreur chargement inventaire'); }
    finally { setReloading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onCreatePlanning = async (data) => {
    try { await axios.post(`${API}/inventory/plannings`, data, { headers: auth() }); toast.success('Planning créé'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur création'); }
  };
  const onDeletePlanning = async (id) => {
    if (!window.confirm('Supprimer ce planning ?')) return;
    try { await axios.delete(`${API}/inventory/plannings/${id}`, { headers: auth() }); reload(); }
    catch (e) { toast.error('Erreur'); }
  };
  const onStartPlanning = async (planning) => {
    if (planning.inventory_id) { setActiveInventoryId(planning.inventory_id); return; }
    try {
      const res = await axios.post(`${API}/inventory/plannings/${planning.id}/start`, {}, { headers: auth() });
      setActiveInventoryId(res.data.inventory_id);
      toast.success(`Inventaire ouvert · ${res.data.products_count} produits`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const onResetPlanning = async (planning) => {
    if (!window.confirm(
      `Réinitialiser l'inventaire du rayon « ${planning.zone_name} » ? `
      + 'Toutes les saisies en cours et les écarts détectés seront supprimés. À utiliser après correction du mapping emplacements.'
    )) return;
    try {
      const r = await axios.post(`${API}/inventory/plannings/${planning.id}/reset`, {}, { headers: auth() });
      toast.success(`Inventaire réinitialisé · ${r.data.deleted_lines} lignes purgées`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur réinitialisation'); }
  };
  const onUpdateActionStatus = async (id, statut) => {
    try {
      await axios.put(`${API}/inventory/actions/${id}`, { statut }, { headers: auth() });
      reload();
    } catch (e) { toast.error('Erreur de mise à jour'); }
  };
  // Génération synchrone de l'URL WhatsApp côté client (pas d'await pour
  // préserver le user gesture sur iOS Safari, et éviter de naviguer la page
  // principale en cas de blocage de popup).
  //
  // Normalisation téléphone alignée sur le backend `_build_whatsapp_url` :
  // on conserve TOUS les chiffres et on préfixe simplement « 225 » si le
  // numéro est en format national 10 chiffres (ex: 0770000000 → 2250770000000).
  // ⚠️ NE PAS retirer le 0 initial — WhatsApp iOS exige le 0 dans le national.
  const buildActionWaUrl = (a) => {
    const raw = a.responsable_whatsapp || '';
    let digits = String(raw).replace(/\D/g, '');
    if (!digits) return null;
    if (digits.startsWith('00225')) digits = digits.slice(2);
    if (!digits.startsWith('225') && digits.length === 10) digits = '225' + digits;
    if (digits.length < 12) return null;
    const isOverdue = a.is_overdue || (a.statut === 'a_faire' && a.date_limite && a.date_limite < new Date().toISOString().slice(0, 10));
    const firstName = (a.responsable_name || '').split(' ')[0] || a.responsable_name || '';
    const product = a.product_name && a.product_name !== '—' ? ` sur le produit « ${a.product_name} »` : '';
    const msg = `Bonjour ${firstName}, rappel : action${product} « ${a.description || ''} » `
      + `${isOverdue ? 'en retard depuis le ' : 'à clôturer avant le '}${a.date_limite || ''}. `
      + 'Merci de mettre à jour le statut dans l\'application.';
    return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
  };

  const onWhatsApp = (action) => {
    const url = buildActionWaUrl(action);
    if (!url) {
      toast.error('Numéro WhatsApp du responsable manquant ou invalide');
      return;
    }
    // Ouverture synchrone DANS le user gesture → iOS/Android préservent le ?text=
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const onCreateZone = async (name) => {
    try { await axios.post(`${API}/inventory/zones`, { name, is_critical: false, mediciel_emplacement_ids: [] }, { headers: auth() }); reload(); }
    catch (e) { toast.error('Erreur'); }
  };
  const onUpdateZone = async (id, payload) => {
    try { await axios.put(`${API}/inventory/zones/${id}`, payload, { headers: auth() }); reload(); }
    catch (e) { toast.error('Erreur'); }
  };
  const onDeleteZone = async (id) => {
    if (!window.confirm('Supprimer cette zone ?')) return;
    try { await axios.delete(`${API}/inventory/zones/${id}`, { headers: auth() }); reload(); }
    catch (e) { toast.error('Erreur'); }
  };
  const onUpdateAgent = async (id, payload) => {
    try { await axios.put(`${API}/inventory/agents/${id}`, payload, { headers: auth() }); reload(); }
    catch (e) { toast.error('Erreur'); }
  };
  const onUpdateAvailability = async (id, restDay, months) => {
    try {
      await axios.put(
        `${API}/inventory/agents/${id}/availability`,
        { rest_day_of_week: restDay, vacation_months: months },
        { headers: auth() }
      );
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur disponibilité'); }
  };
  const onToggleExcludeOp = async (id, name, next) => {
    try {
      await axios.put(
        `${API}/inventory/agents/${id}/exclude-op`,
        { exclude_op: next },
        { headers: auth() }
      );
      toast.success(next
        ? `${name} exclu(e) du round-robin OP1/OP2. Cliquez "Réassigner Cyrille" pour nettoyer les plannings legacy.`
        : `${name} réintégré(e) dans le round-robin OP1/OP2.`,
        { duration: 6000 });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur mise à jour exclusion'); }
  };
  const onSaveEmplacements = async (zoneId, ids) => {
    try {
      await axios.put(
        `${API}/inventory/zones/${zoneId}/mediciel-emplacements`,
        { mediciel_emplacement_ids: ids },
        { headers: auth() }
      );
      toast.success(`${ids.length} emplacement(s) mappé(s)`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur mapping'); }
  };
  const onAutoMapAll = async () => {
    if (!window.confirm(
      'Lancer la suggestion automatique ?\n\n'
      + 'L\'app va analyser les noms de produits dans chaque emplacement Mediciel '
      + 'et proposer une correspondance pour chaque rayon non encore mappé.\n\n'
      + 'Aucune zone déjà mappée ne sera écrasée.'
    )) return;
    try {
      const r = await axios.post(
        `${API}/inventory/zones/auto-map-apply`,
        { min_confidence: 5.0, overwrite: false },
        { headers: auth() }
      );
      const skipped = r.data.skipped || [];
      toast.success(`${r.data.applied} rayon(s) auto-mappés · ${skipped.length} ignoré(s)`);
      if (skipped.length) {
        toast.info(
          `À mapper manuellement : ${skipped.slice(0, 5).map(s => s.zone).join(', ')}${skipped.length > 5 ? '…' : ''}`,
          { duration: 7000 }
        );
      }
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur auto-mapping'); }
  };
  const onImportRegistre = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/inventory/zones/import-mapping-excel`, fd, { headers: auth() });
      toast.success(
        `Registre importé · ${r.data.zones_updated}/27 rayons mappés `
        + `(${r.data.matched_by_ean}/${r.data.rows_in_excel} produits matchés)`
      );
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import registre'); }
  };
  const onUpdateSettings = async (payload) => {
    try { const res = await axios.put(`${API}/inventory/settings`, payload, { headers: auth() }); setSettings(res.data); }
    catch (e) { toast.error('Erreur'); }
  };
  const onGenerateCycle = async (startMonday, cyclesCount) => {
    try {
      const res = await axios.post(
        `${API}/inventory/plannings/generate-cycle`,
        { start_monday: startMonday, cycles_count: cyclesCount, skip_existing: true },
        { headers: auth() }
      );
      const sat = res.data.holiday_shifts || 0;
      const warns = (res.data.warnings || []).length;
      let msg = `${res.data.created} plannings créés · ${res.data.skipped} déjà existants`;
      if (sat) msg += ` · ${sat} décalage(s) férié/week-end`;
      if (warns) msg += ` · ${warns} avertissement(s)`;
      toast.success(msg);
      if (warns) {
        // Show the first 3 warnings as info
        (res.data.warnings || []).slice(0, 3).forEach(w => toast.info(w, { duration: 6000 }));
      }
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération cycle');
    }
  };
  const onRefreshStock = async () => {
    try {
      const res = await axios.post(`${API}/mediciel-realtime/sync`, {}, { headers: auth() });
      const { products_count = 0, duration_seconds = 0, propagation = {} } = res.data || {};
      const propLine = propagation?.error
        ? ` · ⚠ propagation : ${propagation.error}`
        : (propagation?.lines_touched
            ? ` · ${propagation.lines_changed || 0} ligne(s) snapshot mises à jour sur ${propagation.inventories_touched || 0} inventaire(s) en cours`
            : '');
      toast.success(
        `Stock actualisé · ${products_count} produits (${duration_seconds.toFixed?.(1) ?? duration_seconds}s)${propLine}`,
        { duration: 7000 }
      );
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur synchronisation stock');
    }
  };

  const onSnapshotResp = async () => {
    try {
      const res = await axios.post(`${API}/inventory/kpi/responsables/snapshot`, {}, { headers: auth() });
      toast.success(`Snapshot ${res.data.period} ${res.data.created ? 'créé' : 'mis à jour'} (${res.data.count} responsables)`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur snapshot'); }
  };
  const onDownloadRecap = async (from, to) => {
    try {
      const res = await axios.post(
        `${API}/inventory/cycle-recap/pdf`,
        { date_from: from, date_to: to, send_emails: false },
        { headers: auth(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recap_inventaire_${from}_${to}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success('PDF récap téléchargé');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur PDF'); }
  };
  const onSendRecap = async (from, to) => {
    try {
      const res = await axios.post(
        `${API}/inventory/cycle-recap/send`,
        { date_from: from, date_to: to, send_emails: true },
        { headers: auth() }
      );
      const { sent_email = [], sent_whatsapp = [], skipped = [] } = res.data;
      toast.success(`${sent_email.length} email(s) envoyé(s) · ${sent_whatsapp.length} lien(s) WhatsApp générés`);
      if (skipped.length) {
        toast.info(`${skipped.length} agent(s) sans email — utilisez les liens WhatsApp ci-dessous`, { duration: 6000 });
      }
      // Open all whatsapp links in succession (admin can choose per agent)
      sent_whatsapp.slice(0, 6).forEach((w, i) => {
        setTimeout(() => window.open(w.url, '_blank', 'noopener,noreferrer'), i * 400);
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi récap'); }
  };

  // Manager + Admin ont les droits sur toutes les phases du workflow
  const canManagePhases = isAdmin || role === 'manager';

  if (activeInventoryId) {
    return (
      <SaisieView
        inventoryId={activeInventoryId}
        agents={agents}
        enums={enums}
        onClose={() => { setActiveInventoryId(null); reload(); }}
        onCloseInventory={() => { setActiveInventoryId(null); reload(); }}
        currentUser={user}
        canManagePhases={canManagePhases}
      />
    );
  }

  return (
    <div className="space-y-5" data-testid="inventory-tab">
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">SADAStock · Module pharmacien</p>
            <h1 className="text-xl font-serif font-semibold text-zinc-900 mt-0.5">Inventaire tournant avancé</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Procédure qualité V1.0 · Cycle 6 semaines · Stock théorique alimenté en temps réel par Mediciel</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowGuide(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-50 border border-purple-200 text-[#5B3B8C] hover:bg-purple-100 transition text-sm font-medium"
              data-testid="inv-help-btn"
              title="Guide utilisateur"
            >
              <HelpCircle className="w-4 h-4" /> Aide
            </button>
            <ClipboardList className="w-6 h-6 text-[#1B4D3E]" />
          </div>
        </div>
      </div>

      {showGuide && <InventoryGuideDialog onClose={() => setShowGuide(false)} />}

      <div className="flex gap-1 overflow-x-auto pb-2 border-b border-zinc-200">
        {TABS.filter(t => t.restricted !== 'admin_or_manager' || isAdmin || isManager).map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          const allowed = canSeeTab(t.id);
          return (
            <button
              key={t.id}
              onClick={() => allowed && setTab(t.id)}
              disabled={!allowed}
              title={allowed ? t.label : 'Onglet réservé — demandez l\'accès à l\'administrateur'}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition ${
                active
                  ? 'bg-[#1B4D3E] text-white'
                  : allowed
                    ? 'text-zinc-600 hover:bg-zinc-100'
                    : 'text-zinc-400 bg-zinc-50 cursor-not-allowed opacity-60'
              }`}
              data-testid={`inv-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              {!allowed && <Lock className="w-3 h-3 opacity-60" />}
            </button>
          );
        })}
      </div>

      <div>
        {tab === 'dashboard' && (
          <Dashboard
            kpi={kpi}
            unassigned={unassigned}
            onReload={reload}
            onGoSettings={() => setTab('settings')}
          />
        )}
        {tab === 'planning' && (
          <PlanningView
            plannings={plannings} agents={agents} zones={zones} reloading={reloading}
            onCreate={onCreatePlanning} onDelete={onDeletePlanning} onStart={onStartPlanning}
            onReset={onResetPlanning}
            onGenerateCycle={onGenerateCycle}
            onRefreshStock={onRefreshStock}
            currentUser={user}
            isPrivileged={isPrivileged}
            onReload={reload}
          />
        )}
        {tab === 'pilotage' && (isAdmin || isManager) && (
          <InventoryPilotageReport zones={zones} agents={agents} />
        )}
        {tab === 'responsables' && (
          <ResponsablesView
            rows={respKpi}
            history={respHistory}
            onReload={reload}
            onSnapshot={onSnapshotResp}
            onDownloadRecap={onDownloadRecap}
            onSendRecap={onSendRecap}
          />
        )}
        {tab === 'actions' && (
          <ActionsView actions={actions} onUpdateStatus={onUpdateActionStatus} onWhatsApp={onWhatsApp} reloading={reloading} />
        )}
        {tab === 'settings' && (
          <SettingsView
            zones={zones} agents={agents} settings={settings}
            onUpdateZone={onUpdateZone} onCreateZone={onCreateZone} onDeleteZone={onDeleteZone}
            onUpdateAgent={onUpdateAgent} onUpdateSettings={onUpdateSettings}
            onUpdateAvailability={onUpdateAvailability}
            onToggleExcludeOp={onToggleExcludeOp}
            emplacements={emplacements}
            onSaveEmplacements={onSaveEmplacements}
            onAutoMapAll={onAutoMapAll}
            onImportRegistre={onImportRegistre}
          />
        )}
      </div>
    </div>
  );
};

export default InventoryTab;
